import { getApiBaseUrl, getTenantId } from "./runtime-config";

const API_BASE = getApiBaseUrl();
const ADMIN_TENANT_ID = getTenantId();

export type AdminSession = {
  tenantId: string;
  email: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  expiresAt: number;
  planCode: string | null;
  planFeatures: string[];
  /** Clave temporal entregada al contratar: hay que cambiarla antes de operar. */
  mustChangePassword?: boolean;
};

export type AdminAuthProfile = {
  userId: string;
  tenantId: string;
  fullName: string;
  email: string;
  phone: string | null;
  status: string;
  roles: string[];
  modules: string[];
  companyName: string | null;
  planCode: string | null;
  planFeatures: string[];
  subscriptionStatus: string;
  subscriptionOverdue: boolean;
  subscriptionDueDate: string | null;
  availableGateways: string[];
};

export type CustomerListItem = {
  id: string;
  customerType: "domiciliario" | "empresa" | "institucion";
  nombre: string;
  rut: string;
  direccion: string;
  sector: string;
  estado: "activo" | "inactivo" | "moroso" | "cortado";
  telefono: string;
  email: string;
  medidor: string;
  deuda: number;
  contracts: Array<{
    id: string;
    contractNumber: string;
    status: string;
  }>;
};

export type CustomerFormPayload = {
  customerType: "domiciliario" | "empresa" | "institucion";
  customerCategory?: "socio" | "usuario";
  gender?: "masculino" | "femenino";
  rut: string;
  dv: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  birthDate?: string;
  deathDate?: string;
  businessName?: string;
  businessLine?: string;
  address: string;
  phone: string;
  email: string;
  joinDate: string;
  memberNumber?: string;
};

export type CustomerDetail = {
  id: string;
  customerType: "domiciliario" | "empresa" | "institucion";
  customerCategory?: "socio" | "usuario";
  gender?: "masculino" | "femenino";
  dv?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  birthDate?: string;
  deathDate?: string;
  joinDate?: string;
  memberNumber?: string;
  nombre: string;
  rut: string;
  direccion: string;
  estado: "activo" | "inactivo" | "moroso" | "cortado";
  telefono: string;
  email: string;
  rutChanges: CustomerRutChangeItem[];
};

export type CustomerRutChangeItem = {
  id: string;
  previousRut: string;
  newRut: string;
  changedByName: string | null;
  changedAt: string;
};

export type ContractListItem = {
  id: string;
  customerId: string;
  meterId: string;
  sectorId?: string;
  contractNumber: string;
  contractDate: string;
  startAddress: string;
  sector: string;
  status: string;
  tariffName: string;
  meterSerialNumber: string;
  meterType: string;
  meterSealNumber: string;
  customerName?: string;
  billingRecipientName?: string;
  billingDocumentType?: string;
  billingProfile?: ContractBillingProfileItem | null;
  meterBrand?: string;
  meterManufacturingYear?: number | null;
};

export type ContractOwnershipChangeItem = {
  id: string;
  eventType: "deactivate" | "transfer_holder" | string;
  fromStatus: string;
  toStatus: string;
  debtTransferredAmount: number;
  openDocumentsTransferred: number;
  hadPendingReadings: boolean;
  reason: string;
  changedByName: string | null;
  createdAt: string;
  fromCustomerName: string;
  fromCustomerRut: string;
  toCustomerName: string;
  toCustomerRut: string;
};

export type ContractDetail = ContractListItem & {
  ownershipChanges: ContractOwnershipChangeItem[];
  meterChanges: ContractMeterChangeItem[];
};

export type ContractBillingProfileItem = {
  id: string;
  contractId: string;
  recipientRut: string;
  recipientDv: string;
  recipientName: string;
  businessLine: string | null;
  address: string | null;
  commune: string | null;
  city: string | null;
  email: string | null;
  notes: string | null;
};

export type ContractBillingProfilePayload = {
  recipientRut: string;
  recipientDv?: string;
  recipientName: string;
  businessLine?: string;
  address?: string;
  commune?: string;
  city?: string;
  email?: string;
  notes?: string;
};

export type ContractBillingProfileDefaults = {
  rut: string;
  dv: string;
  recipientName: string;
  businessLine: string | null;
  address: string | null;
  commune: string | null;
  city: string | null;
  email: string | null;
};

export type ContractBillingProfileResponse = {
  contractId: string;
  contractNumber: string;
  defaultRecipient: ContractBillingProfileDefaults;
  billingProfile: ContractBillingProfileItem | null;
};

export type ContractMeterChangeItem = {
  id: string;
  readingPeriodId: string | null;
  readingPeriodCode: string | null;
  installedAt: string;
  initialReading: number;
  oldMeterReadingDate: string | null;
  oldMeterLastReading: number | null;
  oldMeterConsumptionM3: number | null;
  chargeToCustomer: boolean;
  chargeType: "smart" | "mechanical" | "custom" | null;
  chargeAmount: number | null;
  reason: string | null;
  newMeter: {
    id: string;
    brand: string;
    meterType: string;
    serialNumber: string;
    sealNumber: string;
    manufacturingYear: number | null;
    status: string;
  } | null;
};

export type TariffOption = {
  id: string;
  code: string;
  name: string;
};

export type TariffTierItem = {
  id: string;
  fromM3: number;
  toM3: number | null;
  unitPrice: number;
  sortOrder: number;
};

export type TariffItem = TariffOption & {
  fixedCharge: number;
  status: string;
  validFrom: string;
  validTo: string;
  tiers: TariffTierItem[];
};

export type TariffMutationPayload = {
  code: string;
  name: string;
  fixedCharge: number;
  status: string;
  validFrom: string;
  validTo?: string;
  tiers: Array<{
    fromM3: number;
    toM3?: number | null;
    unitPrice: number;
    sortOrder: number;
  }>;
};

export type CreateMeterPayload = {
  brand?: string;
  meterType: string;
  serialNumber: string;
  sealNumber?: string;
  manufacturingYear?: number;
  status: "active" | "inactive";
};

export type CreateContractPayload = {
  customerId: string;
  meterId: string;
  tariffId: string;
  contractNumber: string;
  contractDate: string;
  startAddress: string;
  sector?: string;
  status: "active" | "inactive";
  billingDocumentType: "39" | "41" | "33" | "34";
};

export type UpdateContractPayload = {
  meterId?: string;
  tariffId?: string;
  contractDate?: string;
  startAddress?: string;
  sector?: string;
  billingDocumentType?: "39" | "41" | "33" | "34";
};

export type ChangeContractStatusPayload = {
  toStatus: "active" | "inactive";
  reason?: string;
};

export type ChangeContractMeterPayload = {
  readingPeriodId?: string;
  installedAt: string;
  oldMeterReadingDate?: string;
  oldMeterLastReading?: number;
  initialReading?: number;
  chargeToCustomer?: boolean;
  chargeType?: "smart" | "mechanical" | "custom";
  chargeAmount?: number;
  reason?: string;
  newMeterId?: string;
  newMeter?: CreateMeterPayload;
};

export type ContractOwnershipCheck = {
  contractId: string;
  contractNumber: string;
  contractStatus: string;
  customer: {
    id: string;
    name: string;
    rut: string;
  };
  totalDebt: number;
  currentBalance: number;
  overdueBalance: number;
  openDocuments: number;
  activeInstallments: number;
  hasPendingReadingProcess: boolean;
  currentOpenPeriodCode: string | null;
  pendingReason: string | null;
  canDeactivate: boolean;
  canTransfer: boolean;
};

export type ContractOwnershipActionPayload = {
  action: "deactivate" | "transfer";
  targetCustomerId?: string;
  reason?: string;
};

export type MeterReadingItem = {
  id: string;
  readingPeriodId: string;
  contractId: string;
  readAt: string;
  consumptionM3: number;
  previousReading?: number;
  currentReading?: number | null;
  meterSerialNumber?: string;
  customerName?: string;
  sectorName?: string;
  routeName?: string;
  contractNumber?: string;
  assignedDeviceCode?: string | null;
  technicianName?: string | null;
  notes?: string | null;
  observation?: string | null;
  address?: string;
  customerRut?: string;
  status?: string;
};

export type PaymentItem = {
  id: string;
  receiptNumber: string;
  paidAt: string;
  amount: number;
  status: string;
  reference: string;
  methodName: string;
  allocations: Array<{
    billingDocumentId: string;
    allocatedAmount: number;
    billingDocument: { id: string; documentType: string; folio: string | null; dueDate: string };
  }>;
  contract?: { contractNumber: string; customer: { fullName: string; rut: string } };
};

export type InstallmentItem = {
  id: string;
  contractId: string;
  type: string;
  pie: number;
  parentAgreementId: string | null;
  totalAmount: number;
  installments: number;
  installmentAmount: number;
  paidInstallments: number;
  startPeriod: string;
  endPeriod: string | null;
  status: string;
  notes: string | null;
  createdAt: string;
};

export type PieVoucher = {
  folio: string;
  fecha: string;
  hora: string;
  cajero: string;
  cliente: string;
  rut: string;
  contrato: string;
  concepto: string;
  montoPie: number;
  montoTotal: number;
  cuotas: number;
  montoCuota: number;
  periodoInicio: string;
  periodoTermino: string;
  agreementId: string;
};

export type OperationItem = {
  id: string;
  sequenceNumber: number;
  contractId: string;
  customerId: string;
  operationTypeId: string;
  source: string;
  status: string;
  priority: string;
  requestSummary: string;
  diagnosis: string;
  resolution: string;
  scheduledAt: string;
  startedAt: string;
  completedAt: string;
  archivedAt: string | null;
  requestedAt: string;
  operationTypeName: string;
  operationTypeCategory: string;
  contractNumber: string;
  contractAddress: string;
  customerName: string;
  assignments: Array<{
    id: string;
    operarioId: string | null;
    userId: string | null;
    roleInOperation: string;
  }>;
  vehicles: Array<{
    id: string;
    vehicleId: string;
    code: string;
    name: string;
    plate: string;
    status: string;
  }>;
  materials: Array<{
    id: string;
    inventoryItemId: string;
    materialName: string;
    quantityRequested: number;
    quantityUsed: number;
  }>;
};

export type OperationTypeItem = {
  id: string;
  code: string;
  name: string;
  category: string;
};

export type VehicleItem = {
  id: string;
  code: string;
  name: string;
  plate: string;
  status: string;
};

export type OperarioItem = {
  id: string;
  name: string;
  rut: string | null;
  level: string;
  phone: string | null;
  active: boolean;
};

export type WarehouseItem = {
  id: string;
  code: string;
  name: string;
  warehouseType: string;
  isCentral?: boolean;
  active?: boolean;
};

export type InventoryItemOption = {
  id: string;
  sku: string;
  name: string;
  unit: string;
  active: boolean;
  categoryId?: string;
  categoryName?: string;
  minimumStock?: number;
};

export type InventoryCategoryItem = {
  id: string;
  name: string;
  active: boolean;
};

export type InventoryStockBalanceItem = {
  id: string;
  warehouseId: string;
  warehouseName: string;
  inventoryItemId: string;
  itemName: string;
  itemSku: string;
  quantityOnHand: number;
  quantityReserved: number;
  updatedAt: string;
};

export type InventoryMovementItem = {
  id: string;
  warehouseId: string;
  warehouseCode: string;
  warehouseName: string;
  inventoryItemId: string;
  itemSku: string;
  itemName: string;
  movementType: string;
  quantity: number;
  unitCost: number;
  referenceType: string;
  referenceId: string;
  notes: string;
  personName: string;
  createdBy: string;
  createdAt: string;
};

export type SupplierItem = {
  id: string;
  rut: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  active: boolean;
};

export type PurchaseOrderItem = {
  id: string;
  orderNumber: string;
  docType: string;
  docNumber: string;
  docDate: string;
  status: string;
  orderedAt: string;
  totalAmount: number;
  supplierName: string;
  items: Array<{
    inventoryItemId: string;
    itemName: string;
    quantity: number;
    unitCost: number;
    totalCost: number;
  }>;
};

export type StockTransferItem = {
  id: string;
  fromWarehouseId: string;
  fromWarehouseCode: string;
  fromWarehouseName: string;
  toWarehouseId: string;
  toWarehouseCode: string;
  toWarehouseName: string;
  status: string;
  requestedAt: string;
  completedAt: string;
  items: Array<{
    inventoryItemId: string;
    itemSku: string;
    itemName: string;
    quantity: number;
  }>;
};

export type CreateOperationPayload = {
  contractId: string;
  operationTypeId: string;
  source?: string;
  priority?: string;
  requestSummary: string;
  scheduledAt?: string;
};

export type SectorItem = {
  id: string;
  code: string;
  name: string;
};

export type ReadingRouteItem = {
  id: string;
  code: string;
  name: string;
  active: boolean;
  sectorIds: string[];
  contractIds: string[];
};

export type MobileDeviceItem = {
  id: string;
  deviceCode: string;
  name: string;
  status: string;
  lastSyncAt: string;
};

export type ReadingPeriodItem = {
  id: string;
  periodCode: string;
  status: string;
};

export type ReadingAssignmentItem = {
  id: string;
  readingPeriodId: string;
  sectorId: string;
  routeId: string;
  assignedDeviceCode: string;
  assignmentStatus: string;
  sectorName: string;
  routeName: string;
  periodCode: string;
};

export type ReadingPeriodOverviewItem = {
  rowId: string;
  readingId: string | null;
  contractId: string;
  contractNumber: string;
  customerName: string;
  customerRut: string;
  meterSerialNumber: string;
  sectorName: string;
  routeName: string;
  previousReading: number;
  currentReading: number | null;
  consumptionM3: number | null;
  readAt: string | null;
  notes: string | null;
  technicianName: string | null;
  assignedDeviceCode: string | null;
  observation: string | null;
  observationSeverity: string | null;
  status: "registrada" | "pendiente" | "anomalia";
  address: string;
};

export type ReadingPeriodOverview = {
  period: { id: string; periodCode: string; status: string } | null;
  stats: {
    total: number;
    registered: number;
    pending: number;
    anomalies: number;
    averageConsumptionM3: number;
    completionPct: number;
  };
  filters: {
    sectors: string[];
    technicians: string[];
  };
  rows: ReadingPeriodOverviewItem[];
};

export type MobileRouteSessionPoint = {
  sequence: number;
  eventType: string;
  recordedAt: string;
  lat: number;
  lng: number;
};

export type MobileRouteSessionOverviewItem = {
  sessionId: string;
  clientSessionId: string | null;
  assignmentId: string | null;
  routeId: string | null;
  routeName: string;
  operatorName: string | null;
  deviceCode: string;
  deviceName: string;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  status: string;
  source: string;
  pointsCount: number;
  readingPointsCount: number;
  distanceMeters: number;
  startPoint: MobileRouteSessionPoint | null;
  endPoint: MobileRouteSessionPoint | null;
  points: MobileRouteSessionPoint[];
};

export type MobileRouteSessionsOverview = {
  period: { id: string; periodCode: string; status: string } | null;
  stats: {
    totalSessions: number;
    activeSessions: number;
    completedSessions: number;
    totalDurationSeconds: number;
    totalTrackPoints: number;
    totalReadingPoints: number;
  };
  filters: {
    routes: string[];
    operators: string[];
    devices: string[];
    statuses: string[];
  };
  rows: MobileRouteSessionOverviewItem[];
};

export type BillingDocumentItem = {
  id: string;
  readingPeriodId: string;
  contractId: string;
  contractNumber: string;
  contractAddress: string;
  customerName: string;
  documentType: string;
  issueDate: string;
  dueDate: string;
  folio: string;
  totalAmount: number;
  status: string;
  cancellationStatus: string;
  externalError: string;
  lastJobId: string;
  lastJobStatus: string;
  attempts: number;
  pdfUrl?: string;
  xmlUrl?: string;
};

export type BillingCancellationEligibleDocument = {
  id: string;
  contractId: string;
  contractNumber: string;
  customerName: string;
  customerRut: string;
  documentType: string;
  issueDate: string;
  folio: string | null;
  totalAmount: number;
  status: string;
  cancellationStatus: string;
  paymentsCount: number;
  hasPayments: boolean;
  hasCancellation: boolean;
  canCancel: boolean;
  blockingReason: string | null;
};

export type BillingCancellationPreview = {
  canCancel: boolean;
  blockingReason: string | null;
  detail: string;
  originalDocument: {
    id: string;
    contractId: string;
    contractNumber: string;
    customerId: string;
    customerName: string;
    customerRut: string;
    documentType: string;
    folio: string | null;
    issueDate: string;
    dueDate: string;
    status: string;
    cancellationStatus: string;
    netAmount: number;
    taxAmount: number;
    totalAmount: number;
  };
  items: Array<{
    id: string;
    itemType: string;
    description: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }>;
  payments: Array<{
    allocationId: string;
    allocatedAmount: number;
    payment: {
      id: string;
      receiptNumber: string;
      amount: number;
      paidAt: string;
    } | null;
  }>;
  previousCancellations: Array<{
    id: string;
    mode: string;
    status: string;
    creditNoteFolio: string | null;
    createdAt: string;
    creditNoteDocumentId: string | null;
  }>;
};

export type BillingCancellationHistoryItem = {
  id: string;
  mode: string;
  status: string;
  amount: number;
  originalDocumentType: string;
  originalFolio: string | null;
  creditNoteType: string;
  creditNoteFolio: string | null;
  referenceReasonCode: string;
  referenceReasonLabel: string | null;
  detail: string;
  cancellationDate: string;
  processedAt: string | null;
  requestedBy: string | null;
  requestedByName: string | null;
  createdAt: string;
  lastError: string | null;
  contractNumber: string | null;
  customerName: string | null;
  customerRut: string | null;
  originalBillingDocumentId: string | null;
  creditNoteBillingDocumentId: string | null;
  originalDocument: {
    id: string;
    documentType: string;
    folio: string | null;
    issueDate: string;
    totalAmount: number;
    status: string;
    cancellationStatus: string;
  } | null;
  creditNoteDocument: {
    id: string;
    documentType: string;
    folio: string | null;
  } | null;
};

export type ExpenseConceptItem = {
  id: string;
  conceptType: "fixed" | "variable";
  name: string;
  fixedValue: number | null;
  isBillable: boolean;
  isGross: boolean;
  generatesDebt: boolean;
  status: "active" | "inactive";
};

export type ManualChargePaymentPayload = {
  contractId: string;
  paymentMethodCode: string;
  documentType: "33" | "34" | "39" | "41";
  reference?: string;
  transactionNumber?: string;
  backupImageDataUrl?: string;
  items: Array<{
    expenseConceptId: string;
    amount?: number;
  }>;
};

export type FolioAvailabilityItem = {
  type: number;
  required: number;
  available: number;
  folioActual: number | null;
  folioFinal: number | null;
  ok: boolean;
  status: string;
};

export type FolioAvailabilitySummary = {
  readingPeriodId: string;
  forceWithoutReading: boolean;
  requiredTotal: number;
  items: FolioAvailabilityItem[];
};

export type BillingClosurePreviewItem = {
  contractId: string;
  contractNumber: string;
  customerName: string;
  customerRut: string;
  customerAddress: string;
  customerEmail: string;
  contractAddress: string;
  commune: string;
  sector: string;
  serviceNumber: string;
  tariffName: string;
  documentType: "33" | "34" | "39" | "41";
  billedInPeriod: number;
  consumptionM3: number;
  fixedCharge: number;
  variableChargesTotal: number;
  previousBalance: number;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
  currentConsumptionCharge: number;
  updatedConsumptionCharge: number;
  updatedTotalAmount: number;
  differenceAmount: number;
  items: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }>;
  consumptionBreakdown: Array<{
    sortOrder: number;
    billedM3: number;
    unitPrice: number;
    subtotal: number;
  }>;
};

export type PeriodVariableChargeItem = {
  id: string;
  readingPeriodId: string;
  contractId: string;
  contractNumber: string;
  customerName: string;
  expenseConceptId: string;
  expenseConceptName: string;
  amount: number;
  notes: string | null;
};

export type CreateExpenseConceptPayload = {
  conceptType: "fixed" | "variable";
  name: string;
  fixedValue: number | null;
  isBillable: boolean;
  isGross: boolean;
  generatesDebt: boolean;
  status: "active" | "inactive";
};

export type UpdateExpenseConceptPayload = CreateExpenseConceptPayload;

export type CreatePeriodVariableChargesPayload = {
  readingPeriodId: string;
  charges: Array<{
    contractId: string;
    expenseConceptId: string;
    amount: number;
    notes?: string;
  }>;
};

export type InvoiceJobItem = {
  id: string;
  billingDocumentId: string;
  providerName: string;
  status: string;
  attempts: number;
  lastError: string;
};

export type PaymentDebtSearchItem = {
  contractId: string;
  contractNumber: string;
  address: string;
  customer: {
    id: string;
    name: string;
    rut: string;
  };
  debtSummary: {
    documents: number;
    outstandingAmount: number;
    overdueAmount: number;
  };
  matchedDocuments: Array<{
    id: string;
    folio: string | null;
    documentType: string;
    status: string;
    dueDate: string;
    outstandingAmount: number;
  }>;
};

export type PaymentDebtSummary = {
  contractId: string;
  contractNumber: string;
  customer: {
    id: string;
    name: string;
    rut: string;
  };
  debtSummary: {
    documents: number;
    outstandingAmount: number;
    overdueAmount: number;
  };
  documents: Array<{
    id: string;
    folio: string | null;
    documentType: string;
    status: string;
    dueDate: string;
    totalAmount: number;
    paidAmount: number;
    outstandingAmount: number;
  }>;
  lastPayment: {
    id: string;
    amount: number;
    paidAt: string;
    receiptNumber: string;
    paymentMethodName: string | null;
  } | null;
};

export type PaymentMethodItem = {
  id: string;
  code: string;
  name: string;
  isCredit: boolean;
  status: string;
  manualEnabled: boolean;
  onlineEnabled: boolean;
  requiresBackup: boolean;
};

export type CashClosureSnapshot = {
  businessDate: string;
  generatedAt: string;
  paymentsCount: number;
  movementsCount: number;
  totals: {
    payments: number;
    income: number;
    expense: number;
    net: number;
  };
  byPaymentMethod: Array<{
    code: string;
    name: string;
    count: number;
    amount: number;
  }>;
  byMovementType: Array<{
    type: string;
    count: number;
    amount: number;
  }>;
  payments: Array<{
    id: string;
    paidAt: string;
    receiptNumber: string;
    contractNumber: string;
    customerName: string;
    paymentMethodCode: string;
    paymentMethodName: string;
    amount: number;
    reference: string | null;
  }>;
  movements: Array<{
    id: string;
    movementDate: string;
    createdAt: string;
    movementCode: string;
    movementType: string;
    category: string;
    description: string;
    amount: number;
    responsible: string;
    voucherNumber: string | null;
  }>;
};

export type CashClosureItem = {
  id: string;
  closureCode: string;
  businessDate: string;
  closedAt: string;
  notes: string | null;
  paymentsCount: number;
  movementsCount: number;
  totalPayments: number;
  totalIncome: number;
  totalExpense: number;
  netTotal: number;
  closedBy: string | null;
  closedByName: string | null;
  summary: {
    businessDate?: string;
    generatedAt?: string;
    totals?: {
      payments?: number;
      income?: number;
      expense?: number;
      net?: number;
    };
    byPaymentMethod?: Array<{
      code: string;
      name: string;
      count: number;
      amount: number;
    }>;
    byMovementType?: Array<{
      type: string;
      count: number;
      amount: number;
    }>;
    notes?: string | null;
  };
  detail: {
    payments?: CashClosureSnapshot["payments"];
    movements?: CashClosureSnapshot["movements"];
  };
};

export type SettingsSnapshot = {
  organization: {
    companyName?: string;
    companyRut?: string;
    giro?: string;
    region?: string;
    comuna?: string;
    address?: string;
    phone?: string;
    email?: string;
    contractNumberSeed?: number;
    contractNumberStep?: number;
    morosidadMonths?: number;
  } | null;
  billing: {
    billingFrequency?: string;
    cutoffDay?: number;
    dueDay?: number;
  } | null;
  invoiceIntegration: {
    providerName?: string;
    endpoint?: string;
    status?: string;
    extraJson?: Record<string, unknown>;
  } | null;
  security: {
    sessionTimeoutMinutes?: number;
    passwordMinLength?: number;
    passwordRotationDays?: number;
    mfaForAdmins?: boolean;
    lockoutAttempts?: number;
    lockoutMinutes?: number;
    alertChannelsJson?: Record<string, { email?: boolean; sms?: boolean; sistema?: boolean }> | null;
    delinquencyGraceDays?: number;
    abnormalConsumptionThresholdPercent?: number;
  } | null;
};

export type NotificationItem = {
  key: string;
  category: "corte" | "morosidad" | "operaciones" | "facturacion" | "inventario" | "sistema";
  type: "error" | "warning" | "success" | "info";
  title: string;
  description: string;
  createdAt: string;
  link?: string;
  read: boolean;
};

export type SalesCollectionsReport = {
  range: { from: string; to: string };
  sales: { documents: number; amount: number };
  collections: {
    payments: number;
    amount: number;
    byMethod: Array<{ method: string; amount: number }>;
  };
  receivables: {
    currentAmount: number;
    overdueAmount: number;
    outstandingAmount: number;
  };
  collectionRatePct: number;
  monthly: Array<{
    month: string;
    billedAmount: number;
    billedDocuments: number;
    collectedAmount: number;
    collectedPayments: number;
  }>;
};

export type ConsumptionBySectorReport = {
  periodCode: string | null;
  rows: Array<{
    sector: string;
    contracts: number;
    readings: number;
    anomalies: number;
    totalConsumptionM3: number;
    avgConsumptionM3: number;
  }>;
  totals: {
    sectors: number;
    readings: number;
    anomalies: number;
    totalConsumptionM3: number;
  };
};

export type InventoryValuationReport = {
  totalValue: number;
  byWarehouse: Array<{
    warehouseId: string;
    warehouseName: string;
    totalValue: number;
  }>;
  rows: Array<{
    warehouseId: string;
    warehouseName: string;
    inventoryItemId: string;
    sku: string;
    itemName: string;
    quantityOnHand: number;
    unitCost: number;
    totalValue: number;
  }>;
};

export type ReadingsByPeriodReport = {
  rows: Array<{
    readingPeriodId: string;
    periodCode: string;
    status: string;
    readings: number;
    expectedReadings: number;
    completionPct: number;
    anomalies: number;
    totalConsumptionM3: number;
    avgConsumptionM3: number;
    openedAt: string;
    closedAt: string | null;
  }>;
  totals: { periods: number; readings: number; anomalies: number };
};

export type DelinquencyReport = {
  minDays: number;
  summary: {
    contracts: number;
    overdueAmount: number;
    buckets: {
      d1_30: number;
      d31_60: number;
      d61_90: number;
      d90_plus: number;
    };
  };
  contracts: Array<{
    contractId: string;
    contractNumber: string;
    customer: string;
    address: string;
    documents: number;
    overdueAmount: number;
    maxDaysOverdue: number;
  }>;
};

export type CashMovementsReport = {
  range: { from: string; to: string };
  summary: {
    totalIncome: number;
    movements: number;
    byMethod: Array<{ method: string; amount: number }>;
    bySource: Array<{ source: string; amount: number }>;
  };
  movements: Array<{
    paymentId: string;
    paidAt: string;
    contractNumber: string;
    customer: string;
    method: string;
    source: string;
    amount: number;
    allocations: number;
    receiptNumber: string | null;
    reference: string | null;
  }>;
};

export type ExecutiveQuarterlyReport = {
  period: { year: number; quarter: number; from: string; to: string };
  kpis: {
    billedAmount: number;
    collectedAmount: number;
    collectionRatePct: number;
    overdueAmount: number;
    activeDelinquentContracts: number;
    inventoryValue: number;
    avgQuarterConsumptionM3: number;
    operationsCompletedPct: number;
    activeContracts: number;
    activeCustomers: number;
  };
};

export type LegalQuarterlyCategory = {
  code: string;
  label: string;
};

export type LegalQuarterlyAccountingLine = {
  serviceType: "ap" | "as";
  side: "income" | "expense";
  categoryCode: string;
  categoryLabel: string;
  month: number;
  amount: number;
};

export type LegalQuarterlyConnectionMetric = {
  month: number;
  totalConnections: number | null;
  connectionsHalf: number | null;
  connectionsThreeQ: number | null;
  connectionsOne: number | null;
  connectionsTwo: number | null;
  connectionsOther: number | null;
  moroseSocios: number | null;
  moroseUsuarios: number | null;
  overdueSocios: number | null;
  overdueUsuarios: number | null;
  producedM3: number | null;
  billedM3: number | null;
};

export type LegalQuarterlyBankBalance = {
  month: number;
  accountType: "corriente" | "ahorro_vista" | "fondo_mutuo" | "deposito_plazo" | "otro";
  balance: number;
  notes: string | null;
};

export type LegalQuarterlyReport = {
  period: {
    year: number;
    quarter: number;
    monthNumbers: number[];
  };
  organization: {
    companyName: string;
    companyRut: string;
    region: string;
    province: string;
    comuna: string;
  };
  profile: {
    serviceScope: "ap" | "as" | "ap_only";
    preparedByName: string;
    preparedByRole: string;
    submittedAt: string | null;
    notes: string;
  };
  complementary: {
    subsidies: {
      vigentes: number;
      pct100: number;
      pct50: number;
      other: number;
    };
    tariffAp: {
      fixedCharge: number;
      ranges: Array<{
        label: string;
        half: number;
        threeQuarter: number;
        one: number;
        two: number;
      }>;
    };
  };
  categories: {
    incomeAp: LegalQuarterlyCategory[];
    expenseAp: LegalQuarterlyCategory[];
    bankAccountTypes: Array<"corriente" | "ahorro_vista" | "fondo_mutuo" | "deposito_plazo" | "otro">;
  };
  accountingLines: LegalQuarterlyAccountingLine[];
  connectionMetrics: LegalQuarterlyConnectionMetric[];
  bankBalances: LegalQuarterlyBankBalance[];
  consumptionDetail: Array<{
    customerType: string;
    customerId: string;
    customerName: string;
    contractId: string;
    contractNumber: string;
    monthValues: Array<{
      month: number;
      consumptionM3: number;
      billedAmount: number;
    }>;
    totalConsumptionM3: number;
    totalBilledAmount: number;
  }>;
  completion: {
    completedFields: number;
    totalFields: number;
    percent: number;
  };
  validation: {
    status: "ok" | "warning";
    blockers: number;
    warnings: number;
    items: Array<{
      code: string;
      severity: "blocker" | "warning";
      message: string;
      detail: string;
    }>;
  };
};

export type UpsertLegalQuarterlyReportPayload = {
  year: number;
  quarter: number;
  serviceScope?: "ap" | "as" | "ap_only";
  preparedByName?: string | null;
  preparedByRole?: string | null;
  submittedAt?: string | null;
  notes?: string | null;
  complementary?: {
    subsidies?: {
      vigentes?: number | null;
      pct100?: number | null;
      pct50?: number | null;
      other?: number | null;
    };
    tariffAp?: {
      fixedCharge?: number | null;
      ranges?: Array<{
        label: string;
        half?: number | null;
        threeQuarter?: number | null;
        one?: number | null;
        two?: number | null;
      }>;
    };
  };
  accountingLines: LegalQuarterlyAccountingLine[];
  connectionMetrics: LegalQuarterlyConnectionMetric[];
  bankBalances: LegalQuarterlyBankBalance[];
};

export type SystemUserItem = {
  id: string;
  fullName: string;
  email: string;
  status: "active" | "inactive";
  lastLoginAt: string | null;
  role: {
    code: string;
    name: string;
  } | null;
};

export type SystemRoleItem = {
  id: string;
  code: string;
  name: string;
  description: string;
  modules: string[];
};

export type SecurityAuditLogItem = {
  id: string;
  module: string;
  action: string;
  entityName: string;
  entityId: string | null;
  userName: string;
  userEmail: string | null;
  createdAt: string;
};

export type InstitutionalLoanInstallmentItem = {
  id: string;
  installmentNumber: number;
  dueDate: string;
  amount: number;
  principalAmount: number;
  interestAmount: number;
  status: "pagada" | "pendiente" | "vencida";
  paidAt: string | null;
  reference: string | null;
};

export type InstitutionalLoanItem = {
  id: string;
  loanNumber: string;
  lenderName: string;
  loanType: "bancario" | "fosis" | "subdere" | "gore" | "particular";
  purpose: string;
  totalAmount: number;
  paidAmount: number;
  monthlyInstallment: number;
  installmentsPaid: number;
  installmentsTotal: number;
  interestRate: number;
  startDate: string;
  endDate: string;
  status: "vigente" | "pagado" | "mora" | "refinanciado";
  responsible: string;
  collateral: string | null;
  notes: string | null;
  installments: InstitutionalLoanInstallmentItem[];
};

export type CreateInstitutionalLoanPayload = {
  lenderName: string;
  loanType: "bancario" | "fosis" | "subdere" | "gore" | "particular";
  purpose: string;
  totalAmount: number;
  installmentsTotal: number;
  monthlyInstallment?: number;
  installmentsPaid?: number;
  paidAmount?: number;
  interestRate?: number;
  startDate: string;
  endDate?: string;
  status?: "vigente" | "pagado" | "mora" | "refinanciado";
  responsible: string;
  collateral?: string;
  notes?: string;
};

export type UpdateLoanStatusPayload = {
  status: "vigente" | "pagado" | "mora" | "refinanciado";
};

export type UpdateLoanInstallmentPayload = {
  status: "pagada" | "pendiente" | "vencida";
  paidAt?: string | null;
  reference?: string | null;
};

export type PettyCashMovementItem = {
  id: string;
  movementCode: string;
  movementDate: string;
  movementType: "ingreso" | "egreso";
  category: string;
  description: string;
  amount: number;
  responsible: string;
  voucherNumber: string | null;
  notes: string | null;
};

export type CreatePettyCashMovementPayload = {
  movementDate: string;
  movementType: "ingreso" | "egreso";
  category: string;
  description: string;
  amount: number;
  responsible: string;
  voucherNumber?: string;
  notes?: string;
};

export type PettyCashSummary = {
  saldoInicial: number;
  totalIngresos: number;
  totalEgresos: number;
  saldoActual: number;
  movimientosIngresos: number;
  movimientosEgresos: number;
};

export type AprDocumentItem = {
  id: string;
  documentCode: string;
  name: string;
  category: "legal" | "financiero" | "tecnico" | "actas" | "contratos" | "permisos";
  description: string;
  uploadedAt: string;
  expiresAt: string | null;
  sizeLabel: string;
  mimeType: string;
  fileExtension: "pdf" | "xlsx" | "docx" | "jpg" | "zip";
  fileUrl: string | null;
  uploadedBy: string;
  version: string;
  status: "vigente" | "vencido" | "revision";
  tags: string[];
};

export type CreateAprDocumentPayload = {
  name: string;
  category: "legal" | "financiero" | "tecnico" | "actas" | "contratos" | "permisos";
  description: string;
  expiresAt?: string;
  sizeLabel?: string;
  mimeType?: string;
  fileExtension?: "pdf" | "xlsx" | "docx" | "jpg" | "zip";
  fileUrl?: string;
  uploadedBy: string;
  version?: string;
  status?: "vigente" | "vencido" | "revision";
  tags?: string[];
};

export type UpdateAprDocumentPayload = {
  name?: string;
  category?: "legal" | "financiero" | "tecnico" | "actas" | "contratos" | "permisos";
  description?: string;
  expiresAt?: string | null;
  sizeLabel?: string;
  mimeType?: string;
  fileExtension?: "pdf" | "xlsx" | "docx" | "jpg" | "zip";
  fileUrl?: string | null;
  uploadedBy?: string;
  version?: string;
  status?: "vigente" | "vencido" | "revision";
  tags?: string[];
};

type RespuestaLogin = {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tenantId?: string;
  planCode?: string | null;
  planFeatures?: string[];
  mustChangePassword?: boolean;
};

function armarSesion(payload: RespuestaLogin, email: string, tenantId: string): AdminSession {
  return {
    tenantId,
    email,
    accessToken: payload.accessToken,
    refreshToken: payload.refreshToken,
    expiresIn: payload.expiresIn,
    expiresAt: Date.now() + payload.expiresIn * 1000,
    planCode: payload.planCode ?? null,
    planFeatures: payload.planFeatures ?? [],
    mustChangePassword: Boolean(payload.mustChangePassword),
  };
}

/**
 * Ingreso al sistema.
 *
 * Primero se intenta contra el APR que este admin tiene configurado, que es el
 * caso de las instalaciones dedicadas. Si ahi no calza, se resuelve el APR por
 * el correo: en admin.apronline.cl conviven todos los clientes SaaS y no hay un
 * APR fijo al que preguntarle.
 */
export async function loginRequest(input: {
  email: string;
  password: string;
}): Promise<AdminSession> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-tenant-id": ADMIN_TENANT_ID,
    },
    body: JSON.stringify({ email: input.email, password: input.password }),
  });

  if (response.ok) {
    const payload = (await response.json()) as RespuestaLogin;
    return armarSesion(payload, input.email, ADMIN_TENANT_ID);
  }

  // Un 401 aca no significa que la clave este mala: puede ser que el usuario
  // pertenezca a otro APR. Recien despues de intentar por correo se puede decir
  // que las credenciales no sirven.
  const errorConfigurado = ((await response.json().catch(() => ({}))) as { message?: string }).message;

  const porCorreo = await fetch(`${API_BASE}/owner/auth/auto-login`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email: input.email, password: input.password }),
  });

  if (!porCorreo.ok) {
    throw new Error(errorConfigurado ?? "Credenciales inválidas");
  }

  const payload = (await porCorreo.json()) as RespuestaLogin;
  return armarSesion(payload, input.email, payload.tenantId ?? ADMIN_TENANT_ID);
}

export async function refreshSessionRequest(session: AdminSession): Promise<AdminSession> {
  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify({ refreshToken: session.refreshToken }),
  });

  if (!response.ok) {
    throw new Error(`No se pudo renovar sesion (${response.status})`);
  }

  const payload = (await response.json()) as {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };

  return {
    tenantId: session.tenantId,
    email: session.email,
    accessToken: payload.accessToken,
    refreshToken: payload.refreshToken,
    expiresIn: payload.expiresIn,
    expiresAt: Date.now() + payload.expiresIn * 1000,
    planCode: session.planCode,
    planFeatures: session.planFeatures,
    // Renovar el token no cambia la clave: si venia obligado a cambiarla, sigue obligado.
    mustChangePassword: session.mustChangePassword,
  };
}

export async function logoutRequest(session: AdminSession): Promise<void> {
  await fetch(`${API_BASE}/auth/logout`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify({ refreshToken: session.refreshToken }),
  });
}

export async function getAuthProfile(session: AdminSession): Promise<AdminAuthProfile> {
  const response = await fetch(`${API_BASE}/auth/me`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });

  if (!response.ok) {
    throw new Error(`No se pudo cargar perfil (${response.status})`);
  }

  const payload = (await response.json()) as { user?: Record<string, unknown> };
  const user = payload.user ?? {};

  return {
    userId: asString(user.userId),
    tenantId: asString(user.tenantId),
    fullName: asString(user.fullName),
    email: asString(user.email),
    phone: asString(user.phone) || null,
    status: asString(user.status),
    roles: ((user.roles as string[] | undefined) ?? []).map((value) => asString(value)).filter(Boolean),
    modules: ((user.modules as string[] | undefined) ?? []).map((value) => asString(value)).filter(Boolean),
    companyName: asString(user.companyName) || null,
    planCode: asString(user.planCode) || null,
    planFeatures: ((user.planFeatures as string[] | undefined) ?? []).map((value) => asString(value)).filter(Boolean),
    subscriptionStatus: asString(user.subscriptionStatus) || "active",
    subscriptionOverdue: (user.subscriptionOverdue as boolean | undefined) ?? false,
    subscriptionDueDate: asString(user.subscriptionDueDate) || null,
    availableGateways: ((user.availableGateways as string[] | undefined) ?? []).map((v) => asString(v)).filter(Boolean),
  };
}

export async function updateMyProfile(
  session: AdminSession,
  payload: { fullName: string; phone?: string | null },
): Promise<AdminAuthProfile> {
  const response = await fetch(`${API_BASE}/auth/me`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let detail = "";
    try {
      const body = (await response.json()) as { message?: string | string[] };
      detail = Array.isArray(body.message) ? body.message.join(", ") : (body.message ?? "");
    } catch {
      // noop
    }
    throw new Error(detail || `No se pudo actualizar perfil (${response.status})`);
  }

  const json = (await response.json()) as { user?: Record<string, unknown> };
  const user = json.user ?? {};
  return {
    userId: asString(user.userId),
    tenantId: asString(user.tenantId),
    fullName: asString(user.fullName),
    email: asString(user.email),
    phone: asString(user.phone) || null,
    status: asString(user.status),
    roles: ((user.roles as string[] | undefined) ?? []).map((value) => asString(value)).filter(Boolean),
    modules: ((user.modules as string[] | undefined) ?? []).map((value) => asString(value)).filter(Boolean),
  };
}

export async function changeMyPassword(
  session: AdminSession,
  payload: { currentPassword: string; newPassword: string },
): Promise<void> {
  const response = await fetch(`${API_BASE}/auth/me/password`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let detail = "";
    try {
      const body = (await response.json()) as { message?: string | string[] };
      detail = Array.isArray(body.message) ? body.message.join(", ") : (body.message ?? "");
    } catch {
      // noop
    }
    throw new Error(detail || `No se pudo actualizar contraseÃ±a (${response.status})`);
  }
}

export async function listCustomers(
  session: AdminSession,
  options?: { q?: string; limit?: number },
): Promise<CustomerListItem[]> {
  const query = new URLSearchParams();
  if (options?.q?.trim()) query.set("q", options.q.trim());
  if (options?.limit && Number.isFinite(options.limit)) query.set("limit", String(options.limit));
  const suffix = query.size > 0 ? `?${query.toString()}` : "";

  const response = await fetch(`${API_BASE}/customers${suffix}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });

  if (!response.ok) {
    throw new Error(`No se pudo cargar clientes (${response.status})`);
  }

  const data = (await response.json()) as Array<Record<string, unknown>>;
  return data.map(mapCustomer);
}

export async function createCustomer(session: AdminSession, payload: CustomerFormPayload): Promise<CustomerListItem> {
  const response = await fetch(`${API_BASE}/customers`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`No se pudo crear cliente (${response.status})`);
  }
  return mapCustomer((await response.json()) as Record<string, unknown>);
}

export async function getCustomerById(session: AdminSession, customerId: string): Promise<CustomerDetail> {
  const response = await fetch(`${API_BASE}/customers/${customerId}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) {
    throw new Error(`No se pudo cargar cliente (${response.status})`);
  }
  const row = (await response.json()) as Record<string, unknown>;
  const mapped = mapCustomer(row);
  return {
    id: mapped.id,
    customerType: mapped.customerType,
    customerCategory: asString(row.customerCategory) === "socio" ? "socio" : "usuario",
    gender: asString(row.gender) === "femenino" ? "femenino" : asString(row.gender) === "masculino" ? "masculino" : undefined,
    dv: asString(row.dv) || undefined,
    firstName: asString(row.firstName) || undefined,
    lastName: asString(row.lastName) || undefined,
    middleName: asString(row.middleName) || undefined,
    birthDate: asString(row.birthDate) || undefined,
    deathDate: asString(row.deathDate) || undefined,
    joinDate: asString(row.joinDate) || undefined,
    memberNumber: asString(row.memberNumber) || undefined,
    nombre: mapped.nombre,
    rut: mapped.rut,
    direccion: mapped.direccion,
    estado: mapped.estado,
    telefono: mapped.telefono,
    email: mapped.email,
    rutChanges: ((row.rutChanges as Array<Record<string, unknown>> | undefined) ?? []).map((change) => ({
      id: asString(change.id),
      previousRut: normalizeRut(asString(change.previousRut), asString(change.previousDv)),
      newRut: normalizeRut(asString(change.newRut), asString(change.newDv)),
      changedByName: asString(change.changedByName) || null,
      changedAt: asString(change.changedAt),
    })),
  };
}

export async function updateCustomer(
  session: AdminSession,
  customerId: string,
  payload: CustomerFormPayload,
): Promise<CustomerListItem> {
  const response = await fetch(`${API_BASE}/customers/${customerId}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`No se pudo actualizar cliente (${response.status})`);
  }
  return mapCustomer((await response.json()) as Record<string, unknown>);
}

export async function updateCustomerRut(
  session: AdminSession,
  customerId: string,
  rut: string,
): Promise<CustomerListItem> {
  const response = await fetch(`${API_BASE}/customers/${customerId}/rut`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify({ rut }),
  });
  if (!response.ok) {
    throw new Error(`No se pudo actualizar RUT de cliente (${response.status})`);
  }
  return mapCustomer((await response.json()) as Record<string, unknown>);
}

export async function listContracts(
  session: AdminSession,
  options?: { customerId?: string; status?: string; q?: string; limit?: number },
): Promise<ContractListItem[]> {
  const query = new URLSearchParams();
  if (options?.customerId?.trim()) query.set("customerId", options.customerId.trim());
  if (options?.status?.trim()) query.set("status", options.status.trim());
  if (options?.q?.trim()) query.set("q", options.q.trim());
  if (options?.limit && Number.isFinite(options.limit)) query.set("limit", String(options.limit));
  const suffix = query.size > 0 ? `?${query.toString()}` : "";

  const response = await fetch(`${API_BASE}/contracts${suffix}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) {
    throw new Error(`No se pudo cargar contratos (${response.status})`);
  }
  const data = (await response.json()) as Array<Record<string, unknown>>;
  return data.map(mapContract);
}

export async function getContractById(session: AdminSession, contractId: string): Promise<ContractDetail> {
  const response = await fetch(`${API_BASE}/contracts/${contractId}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) {
    throw new Error(`No se pudo cargar contrato (${response.status})`);
  }
  const row = (await response.json()) as Record<string, unknown>;
  const mapped = mapContract(row);
  return {
    ...mapped,
    meterBrand: asString((row.meter as Record<string, unknown> | undefined)?.brand),
    meterManufacturingYear:
      (row.meter as Record<string, unknown> | undefined)?.manufacturingYear == null
        ? null
        : Number((row.meter as Record<string, unknown>).manufacturingYear),
    ownershipChanges: ((row.ownershipChanges as Array<Record<string, unknown>> | undefined) ?? []).map((change) => ({
      id: asString(change.id),
      eventType: asString(change.eventType),
      fromStatus: asString(change.fromStatus),
      toStatus: asString(change.toStatus),
      debtTransferredAmount: Number(change.debtTransferredAmount ?? 0),
      openDocumentsTransferred: Number(change.openDocumentsTransferred ?? 0),
      hadPendingReadings: Boolean(change.hadPendingReadings),
      reason: asString(change.reason),
      changedByName: asString(change.changedByName) || null,
      createdAt: asString(change.createdAt),
      fromCustomerName: asString((change.fromCustomer as Record<string, unknown> | undefined)?.fullName),
      fromCustomerRut: normalizeRut(
        asString((change.fromCustomer as Record<string, unknown> | undefined)?.rut),
        asString((change.fromCustomer as Record<string, unknown> | undefined)?.dv),
      ),
      toCustomerName: asString((change.toCustomer as Record<string, unknown> | undefined)?.fullName),
      toCustomerRut: normalizeRut(
        asString((change.toCustomer as Record<string, unknown> | undefined)?.rut),
        asString((change.toCustomer as Record<string, unknown> | undefined)?.dv),
      ),
    })),
    meterChanges: ((row.meterChanges as Array<Record<string, unknown>> | undefined) ?? []).map((change) => {
      const newMeter = change.newMeter as Record<string, unknown> | undefined;
      const readingPeriod = change.readingPeriod as Record<string, unknown> | undefined;
      const chargeType = asString(change.chargeType);
      return {
        id: asString(change.id),
        readingPeriodId: asString(change.readingPeriodId) || null,
        readingPeriodCode: asString(readingPeriod?.periodCode) || null,
        installedAt: asString(change.installedAt),
        initialReading: Number(change.initialReading ?? 0),
        oldMeterReadingDate: asString(change.oldMeterReadingDate) || null,
        oldMeterLastReading: change.oldMeterLastReading == null ? null : Number(change.oldMeterLastReading),
        oldMeterConsumptionM3: change.oldMeterConsumptionM3 == null ? null : Number(change.oldMeterConsumptionM3),
        chargeToCustomer: Boolean(change.chargeToCustomer),
        chargeType: chargeType === "smart" || chargeType === "mechanical" || chargeType === "custom" ? chargeType : null,
        chargeAmount: change.chargeAmount == null ? null : Number(change.chargeAmount),
        reason: asString(change.reason) || null,
        newMeter: newMeter
          ? {
              id: asString(newMeter.id),
              brand: asString(newMeter.brand),
              meterType: asString(newMeter.meterType),
              serialNumber: asString(newMeter.serialNumber),
              sealNumber: asString(newMeter.sealNumber),
              manufacturingYear: newMeter.manufacturingYear == null ? null : Number(newMeter.manufacturingYear),
              status: asString(newMeter.status),
            }
          : null,
      };
    }),
  };
}

export async function getContractBillingProfile(
  session: AdminSession,
  contractId: string,
): Promise<ContractBillingProfileResponse> {
  const response = await fetch(`${API_BASE}/contracts/${contractId}/billing-profile`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) {
    throw new Error(`No se pudo cargar perfil de facturacion (${response.status})`);
  }
  const row = (await response.json()) as Record<string, unknown>;
  const defaultRecipient = (row.defaultRecipient as Record<string, unknown> | undefined) ?? {};
  return {
    contractId: asString(row.contractId),
    contractNumber: asString(row.contractNumber),
    defaultRecipient: {
      rut: asString(defaultRecipient.rut),
      dv: asString(defaultRecipient.dv),
      recipientName: asString(defaultRecipient.recipientName),
      businessLine: asString(defaultRecipient.businessLine) || null,
      address: asString(defaultRecipient.address) || null,
      commune: asString(defaultRecipient.commune) || null,
      city: asString(defaultRecipient.city) || null,
      email: asString(defaultRecipient.email) || null,
    },
    billingProfile: mapContractBillingProfile(row.billingProfile as Record<string, unknown> | null | undefined),
  };
}

export async function upsertContractBillingProfile(
  session: AdminSession,
  contractId: string,
  payload: ContractBillingProfilePayload,
): Promise<ContractBillingProfileItem> {
  const response = await fetch(`${API_BASE}/contracts/${contractId}/billing-profile`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`No se pudo guardar perfil de facturacion (${response.status})`);
  }
  return mapContractBillingProfile((await response.json()) as Record<string, unknown>) as ContractBillingProfileItem;
}

export async function deleteContractBillingProfile(
  session: AdminSession,
  contractId: string,
): Promise<{ deleted: boolean }> {
  const response = await fetch(`${API_BASE}/contracts/${contractId}/billing-profile`, {
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) {
    throw new Error(`No se pudo eliminar perfil de facturacion (${response.status})`);
  }
  const row = (await response.json()) as Record<string, unknown>;
  return { deleted: Boolean(row.deleted) };
}

export async function listTariffs(session: AdminSession): Promise<TariffOption[]> {
  const response = await fetch(`${API_BASE}/tariffs`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) {
    throw new Error(`No se pudo cargar tarifas (${response.status})`);
  }
  const data = (await response.json()) as Array<Record<string, unknown>>;
  return data.map((row) => ({
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
  }));
}

export async function listTariffItems(session: AdminSession): Promise<TariffItem[]> {
  const response = await fetch(`${API_BASE}/tariffs`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) {
    throw new Error(`No se pudo cargar tarifas (${response.status})`);
  }
  const data = (await response.json()) as Array<Record<string, unknown>>;
  return data.map((row) => ({
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
    fixedCharge: Number(row.fixedCharge ?? 0),
    status: asString(row.status),
    validFrom: asString(row.validFrom),
    validTo: asString(row.validTo),
    tiers: ((row.tiers as Array<Record<string, unknown>> | undefined) ?? []).map((tier) => ({
      id: asString(tier.id),
      fromM3: Number(tier.fromM3 ?? 0),
      toM3: tier.toM3 == null ? null : Number(tier.toM3),
      unitPrice: Number(tier.unitPrice ?? 0),
      sortOrder: Number(tier.sortOrder ?? 0),
    })),
  }));
}

export async function createTariff(session: AdminSession, payload: TariffMutationPayload): Promise<TariffItem> {
  const response = await fetch(`${API_BASE}/tariffs`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear tarifa (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
    fixedCharge: Number(row.fixedCharge ?? 0),
    status: asString(row.status),
    validFrom: asString(row.validFrom),
    validTo: asString(row.validTo),
    tiers: ((row.tiers as Array<Record<string, unknown>> | undefined) ?? []).map((tier) => ({
      id: asString(tier.id),
      fromM3: Number(tier.fromM3 ?? 0),
      toM3: tier.toM3 == null ? null : Number(tier.toM3),
      unitPrice: Number(tier.unitPrice ?? 0),
      sortOrder: Number(tier.sortOrder ?? 0),
    })),
  };
}

export async function updateTariff(
  session: AdminSession,
  id: string,
  payload: TariffMutationPayload,
): Promise<TariffItem> {
  const response = await fetch(`${API_BASE}/tariffs/${id}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar tarifa (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
    fixedCharge: Number(row.fixedCharge ?? 0),
    status: asString(row.status),
    validFrom: asString(row.validFrom),
    validTo: asString(row.validTo),
    tiers: ((row.tiers as Array<Record<string, unknown>> | undefined) ?? []).map((tier) => ({
      id: asString(tier.id),
      fromM3: Number(tier.fromM3 ?? 0),
      toM3: tier.toM3 == null ? null : Number(tier.toM3),
      unitPrice: Number(tier.unitPrice ?? 0),
      sortOrder: Number(tier.sortOrder ?? 0),
    })),
  };
}

export async function deleteTariff(session: AdminSession, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/tariffs/${id}`, {
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo eliminar tarifa (${response.status})`);
}

export async function createMeter(session: AdminSession, payload: CreateMeterPayload): Promise<{ id: string }> {
  const response = await fetch(`${API_BASE}/meters`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`No se pudo crear medidor (${response.status})`);
  }
  const data = (await response.json()) as Record<string, unknown>;
  return { id: asString(data.id) };
}

export async function createContract(session: AdminSession, payload: CreateContractPayload): Promise<ContractListItem> {
  const response = await fetch(`${API_BASE}/contracts`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`No se pudo crear contrato (${response.status})`);
  }
  const created = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(created.id),
    customerId: asString(created.customerId),
    meterId: asString(created.meterId),
    contractNumber: asString(created.contractNumber),
    contractDate: asString(created.contractDate),
    startAddress: asString(created.startAddress),
    sector: asString(created.sector),
    status: asString(created.status),
    tariffName: "",
    meterSerialNumber: "",
    meterType: "",
    meterSealNumber: "",
  };
}

export async function updateContract(
  session: AdminSession,
  contractId: string,
  payload: UpdateContractPayload,
): Promise<void> {
  const response = await fetch(`${API_BASE}/contracts/${contractId}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`No se pudo actualizar contrato (${response.status})`);
  }
}

export async function changeContractStatus(
  session: AdminSession,
  contractId: string,
  payload: ChangeContractStatusPayload,
): Promise<void> {
  const response = await fetch(`${API_BASE}/contracts/${contractId}/status`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`No se pudo actualizar estado de contrato (${response.status})`);
  }
}

export async function changeContractMeter(
  session: AdminSession,
  contractId: string,
  payload: ChangeContractMeterPayload,
): Promise<void> {
  const response = await fetch(`${API_BASE}/contracts/${contractId}/meter`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let detail = "";
    try {
      const body = (await response.json()) as { message?: string | string[] };
      detail = Array.isArray(body.message) ? body.message.join(", ") : body.message ?? "";
    } catch {
      detail = "";
    }
    throw new Error(detail || `No se pudo registrar cambio de medidor (${response.status})`);
  }
}

export async function updateMeter(
  session: AdminSession,
  meterId: string,
  payload: { serialNumber?: string; sealNumber?: string; meterType?: string; status?: string },
): Promise<void> {
  const response = await fetch(`${API_BASE}/meters/${encodeURIComponent(meterId)}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar medidor (${response.status})`);
}

export async function evaluateContractOwnershipAction(
  session: AdminSession,
  contractId: string,
): Promise<ContractOwnershipCheck> {
  const response = await fetch(`${API_BASE}/contracts/${contractId}/ownership-check`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) {
    throw new Error(`No se pudo evaluar contrato (${response.status})`);
  }
  const row = (await response.json()) as Record<string, unknown>;
  return {
    contractId: asString(row.contractId),
    contractNumber: asString(row.contractNumber),
    contractStatus: asString(row.contractStatus),
    customer: {
      id: asString((row.customer as Record<string, unknown> | undefined)?.id),
      name: asString((row.customer as Record<string, unknown> | undefined)?.name),
      rut: asString((row.customer as Record<string, unknown> | undefined)?.rut),
    },
    totalDebt: Number(row.totalDebt ?? 0),
    currentBalance: Number(row.currentBalance ?? 0),
    overdueBalance: Number(row.overdueBalance ?? 0),
    openDocuments: Number(row.openDocuments ?? 0),
    activeInstallments: Number(row.activeInstallments ?? 0),
    hasPendingReadingProcess: Boolean(row.hasPendingReadingProcess),
    currentOpenPeriodCode: asString(row.currentOpenPeriodCode) || null,
    pendingReason: asString(row.pendingReason) || null,
    canDeactivate: Boolean(row.canDeactivate),
    canTransfer: Boolean(row.canTransfer),
  };
}

export async function executeContractOwnershipAction(
  session: AdminSession,
  contractId: string,
  payload: ContractOwnershipActionPayload,
): Promise<void> {
  const response = await fetch(`${API_BASE}/contracts/${contractId}/ownership-action`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let detail = "";
    try {
      const body = (await response.json()) as { message?: string | string[] };
      detail = Array.isArray(body.message) ? body.message.join(", ") : body.message ?? "";
    } catch {
      // noop
    }
    throw new Error(detail || `No se pudo ejecutar accion sobre contrato (${response.status})`);
  }
}

export async function listMeterReadings(session: AdminSession, readingPeriodId?: string): Promise<MeterReadingItem[]> {
  const query = readingPeriodId ? `?readingPeriodId=${encodeURIComponent(readingPeriodId)}` : "";
  const response = await fetch(`${API_BASE}/meter-readings${query}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar lecturas (${response.status})`);
  const data = (await response.json()) as Array<Record<string, unknown>>;
  return data.map((row) => {
    const contract = (row.contract as Record<string, unknown> | undefined) ?? {};
    const customer = (contract.customer as Record<string, unknown> | undefined) ?? {};
    const meter = (contract.meter as Record<string, unknown> | undefined) ?? {};
    const observation = (row.observation as Record<string, unknown> | undefined) ?? {};
    return {
      id: asString(row.id),
      readingPeriodId: asString(row.readingPeriodId),
      contractId: asString(row.contractId),
      readAt: asString(row.readAt),
      consumptionM3: Number(row.consumptionM3 ?? 0),
      previousReading: Number(row.previousReading ?? 0),
      currentReading: row.currentReading == null ? null : Number(row.currentReading),
      meterSerialNumber: asString(meter.serialNumber),
      customerName: asString(customer.fullName),
      sectorName: asString(contract.sector),
      routeName: asString(contract.sector),
      contractNumber: asString(contract.contractNumber),
      assignedDeviceCode: null,
      technicianName: null,
      notes: asString(row.notes) || null,
      observation: asString(observation.name) || null,
      address: asString(contract.startAddress),
      customerRut: `${asString(customer.rut)}-${asString(customer.dv)}`.replace(/^-$/, ""),
      status: asString(row.readingStatus),
    };
  });
}

export async function getReadingPeriodOverview(
  session: AdminSession,
  readingPeriodId?: string,
): Promise<ReadingPeriodOverview> {
  const query = readingPeriodId ? `?readingPeriodId=${encodeURIComponent(readingPeriodId)}` : "";
  const response = await fetch(`${API_BASE}/meter-readings/overview${query}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar resumen del periodo (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapReadingPeriodOverview(row);
}

export async function getMobileRouteSessionsOverview(
  session: AdminSession,
  readingPeriodId?: string,
): Promise<MobileRouteSessionsOverview> {
  const query = readingPeriodId ? `?readingPeriodId=${encodeURIComponent(readingPeriodId)}` : "";
  const response = await fetch(`${API_BASE}/mobile/route-sessions/overview${query}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar seguimiento terreno (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapMobileRouteSessionsOverview(row);
}

export async function releaseMeterReading(
  session: AdminSession,
  payload: { readingPeriodId: string; contractId: string },
): Promise<void> {
  const response = await fetch(`${API_BASE}/meter-readings/release`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo liberar lectura (${response.status})`);
}

export async function updateMeterReading(
  session: AdminSession,
  id: string,
  payload: {
    currentReading?: number;
    readAt?: string;
    readBy?: string;
    readingStatus?: string;
    notes?: string | null;
  },
): Promise<void> {
  const response = await fetch(`${API_BASE}/meter-readings/${id}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let detail = "";
    try {
      const body = (await response.json()) as { message?: string | string[] };
      detail = Array.isArray(body.message) ? body.message.join(", ") : body.message ?? "";
    } catch {
      detail = "";
    }
    throw new Error(detail || `No se pudo actualizar lectura (${response.status})`);
  }
}

export async function createMeterReading(
  session: AdminSession,
  payload: {
    readingPeriodId: string;
    contractId: string;
    currentReading: number;
    readAt?: string;
    readBy?: string;
    readingStatus?: string;
    notes?: string | null;
  },
): Promise<void> {
  const response = await fetch(`${API_BASE}/meter-readings`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let detail = "";
    try {
      const body = (await response.json()) as { message?: string | string[] };
      detail = Array.isArray(body.message) ? body.message.join(", ") : body.message ?? "";
    } catch {
      detail = "";
    }
    throw new Error(detail || `No se pudo registrar lectura (${response.status})`);
  }
}

export async function listPaymentsByContract(session: AdminSession, contractId: string): Promise<PaymentItem[]> {
  const response = await fetch(`${API_BASE}/payments?contractId=${encodeURIComponent(contractId)}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar pagos (${response.status})`);
  const data = (await response.json()) as Array<Record<string, unknown>>;
  return data.map((row) => {
    const method = (row.paymentMethod as Record<string, unknown> | undefined) ?? {};
    const allocations = ((row.allocations as unknown[]) ?? []).map((a) => {
      const al = a as Record<string, unknown>;
      const bd = (al.billingDocument ?? {}) as Record<string, unknown>;
      return {
        billingDocumentId: asString(al.billingDocumentId),
        allocatedAmount: Number(al.allocatedAmount ?? 0),
        billingDocument: {
          id: asString(bd.id),
          documentType: asString(bd.documentType),
          folio: bd.folio ? String(bd.folio) : null,
          dueDate: asString(bd.dueDate),
        },
      };
    });
    const contract = row.contract ? (row.contract as Record<string, unknown>) : null;
    const customer = contract?.customer ? (contract.customer as Record<string, unknown>) : null;
    return {
      id: asString(row.id),
      receiptNumber: asString(row.receiptNumber),
      paidAt: asString(row.paidAt),
      amount: Number(row.amount ?? 0),
      status: asString(row.status),
      reference: asString(row.reference),
      methodName: asString(method.name),
      allocations,
      contract: contract ? {
        contractNumber: asString(contract.contractNumber),
        customer: { fullName: customer ? asString(customer.fullName) : "", rut: customer ? asString(customer.rut) : "" },
      } : undefined,
    };
  });
}

export async function listInstallmentsByContract(
  session: AdminSession,
  contractId: string,
  type?: string,
): Promise<InstallmentItem[]> {
  const params = new URLSearchParams({ contractId });
  if (type) params.set("type", type);
  const response = await fetch(`${API_BASE}/collections/installments?${params.toString()}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar compromisos (${response.status})`);
  const data = (await response.json()) as Array<Record<string, unknown>>;
  return data.map((row) => mapInstallmentRow(row));
}

function mapInstallmentRow(row: Record<string, unknown>): InstallmentItem {
  return {
    id: asString(row.id),
    contractId: asString(row.contractId),
    type: asString(row.type ?? "convenio"),
    pie: Number(row.pie ?? 0),
    parentAgreementId: row.parentAgreementId ? asString(row.parentAgreementId) : null,
    totalAmount: Number(row.totalAmount ?? 0),
    installments: Number(row.installments ?? 0),
    installmentAmount: Number(row.installmentAmount ?? 0),
    paidInstallments: Number(row.paidInstallments ?? 0),
    startPeriod: asString(row.startPeriod),
    endPeriod: row.endPeriod ? asString(row.endPeriod) : null,
    status: asString(row.status),
    notes: row.notes ? asString(row.notes) : null,
    createdAt: asString(row.createdAt),
  };
}

export async function createInstallmentAgreement(
  session: AdminSession,
  payload: {
    contractId: string;
    type?: string;
    pie?: number;
    piePaymentMethodCode?: string;
    parentAgreementId?: string;
    totalAmount: number;
    installments: number;
    startPeriod: string;
    endPeriod?: string;
    notes?: string;
  },
): Promise<{ item: InstallmentItem; voucherData: PieVoucher | null }> {
  const response = await fetch(`${API_BASE}/collections/installments`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear el registro (${response.status})`);
  const data = (await response.json()) as Record<string, unknown>;
  return {
    item: mapInstallmentRow(data),
    voucherData: (data.voucherData as PieVoucher | null) ?? null,
  };
}

export async function getInstallmentVoucher(session: AdminSession, id: string): Promise<PieVoucher> {
  const response = await fetch(`${API_BASE}/collections/installments/${encodeURIComponent(id)}/voucher`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo obtener el voucher (${response.status})`);
  return (await response.json()) as PieVoucher;
}

export async function updateInstallmentStatus(
  session: AdminSession,
  id: string,
  status: string,
): Promise<void> {
  const response = await fetch(`${API_BASE}/collections/installments/${encodeURIComponent(id)}/status`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify({ status }),
  });
  if (!response.ok) {
    let detail = "";
    try {
      const body = (await response.json()) as { message?: string | string[] };
      detail = Array.isArray(body.message) ? body.message.join(", ") : (body.message ?? "");
    } catch {
      // noop
    }
    throw new Error(detail || `No se pudo actualizar el convenio (${response.status})`);
  }
}

export async function listOperations(session: AdminSession, archived = false): Promise<OperationItem[]> {
  const url = archived ? `${API_BASE}/operations?archived=true` : `${API_BASE}/operations`;
  const response = await fetch(url, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar atenciones (${response.status})`);
  const data = (await response.json()) as Array<Record<string, unknown>>;
  return data.map(mapOperation);
}

export async function archiveOperation(session: AdminSession, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/operations/${id}/archive`, {
    method: "POST",
    headers: { authorization: `Bearer ${session.accessToken}`, "x-tenant-id": session.tenantId },
  });
  if (!response.ok) throw new Error(`No se pudo archivar la OT (${response.status})`);
}

export async function unarchiveOperation(session: AdminSession, id: string): Promise<OperationItem> {
  const response = await fetch(`${API_BASE}/operations/${id}/unarchive`, {
    method: "POST",
    headers: { authorization: `Bearer ${session.accessToken}`, "x-tenant-id": session.tenantId },
  });
  if (!response.ok) throw new Error(`No se pudo desarchivar la OT (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapOperation(row);
}

export async function getOperationById(session: AdminSession, id: string): Promise<OperationItem> {
  const response = await fetch(`${API_BASE}/operations/${id}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar operaciÃ³n (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapOperation(row);
}

export async function listOperationTypes(session: AdminSession): Promise<OperationTypeItem[]> {
  const response = await fetch(`${API_BASE}/operations/types`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar tipos de operación (${response.status})`);
  const data = (await response.json()) as Array<Record<string, unknown>>;
  return data.map((row) => ({
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
    category: asString(row.category),
  }));
}

export async function createOperationType(
  session: AdminSession,
  payload: { code: string; name: string; category: string },
): Promise<OperationTypeItem> {
  const response = await fetch(`${API_BASE}/operations/types`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear tipo de operación (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return { id: asString(row.id), code: asString(row.code), name: asString(row.name), category: asString(row.category) };
}

export async function updateOperationType(
  session: AdminSession,
  id: string,
  payload: { name: string; category: string },
): Promise<OperationTypeItem> {
  const response = await fetch(`${API_BASE}/operations/types/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar tipo de operación (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return { id: asString(row.id), code: asString(row.code), name: asString(row.name), category: asString(row.category) };
}

export async function deleteOperationType(session: AdminSession, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/operations/types/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo eliminar tipo de operación (${response.status})`);
}

export async function createOperation(
  session: AdminSession,
  payload: CreateOperationPayload,
): Promise<OperationItem> {
  const response = await fetch(`${API_BASE}/operations`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear operaciÃ³n (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapOperation(row);
}

export async function reviewOperation(
  session: AdminSession,
  id: string,
  payload: { diagnosis?: string; notes?: string },
): Promise<OperationItem> {
  const response = await fetch(`${API_BASE}/operations/${id}/review`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo revisar operaciÃ³n (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapOperation(row);
}

export async function startOperation(
  session: AdminSession,
  id: string,
  payload: { currentReading?: number; notes?: string },
): Promise<OperationItem> {
  const response = await fetch(`${API_BASE}/operations/${id}/start`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo iniciar operaciÃ³n (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapOperation(row);
}

export async function completeOperation(
  session: AdminSession,
  id: string,
  payload: { resolution: string; currentReading?: number; notes?: string },
): Promise<OperationItem> {
  const response = await fetch(`${API_BASE}/operations/${id}/complete`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo completar operaciÃ³n (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapOperation(row);
}

export async function listVehicles(session: AdminSession): Promise<VehicleItem[]> {
  const response = await fetch(`${API_BASE}/vehicles`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar vehÃ­culos (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
    plate: asString(row.plate),
    status: asString(row.status),
  }));
}

export async function createVehicle(
  session: AdminSession,
  payload: { code: string; name: string; plate: string; status?: string; warehouseId?: string },
): Promise<VehicleItem> {
  const response = await fetch(`${API_BASE}/vehicles`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear el vehículo (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return { id: asString(row.id), code: asString(row.code), name: asString(row.name), plate: asString(row.plate), status: asString(row.status) };
}

export async function updateVehicle(
  session: AdminSession,
  id: string,
  payload: { name?: string; plate?: string; status?: string; warehouseId?: string },
): Promise<VehicleItem> {
  const response = await fetch(`${API_BASE}/vehicles/${id}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar el vehículo (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return { id: asString(row.id), code: asString(row.code), name: asString(row.name), plate: asString(row.plate), status: asString(row.status) };
}

export async function deleteVehicle(session: AdminSession, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/vehicles/${id}`, {
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    throw new Error((body.message as string | undefined) ?? `No se pudo eliminar el vehículo (${response.status})`);
  }
}

export async function listOperarios(session: AdminSession): Promise<OperarioItem[]> {
  const response = await fetch(`${API_BASE}/operarios`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar operarios (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    id: asString(row.id),
    name: asString(row.name),
    rut: (row.rut as string | null) ?? null,
    level: asString(row.level),
    phone: (row.phone as string | null) ?? null,
    active: Boolean(row.active),
  }));
}

export async function createOperario(
  session: AdminSession,
  payload: { name: string; rut?: string; level?: string; phone?: string },
): Promise<OperarioItem> {
  const response = await fetch(`${API_BASE}/operarios`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear el operario (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return { id: asString(row.id), name: asString(row.name), rut: (row.rut as string | null) ?? null, level: asString(row.level), phone: (row.phone as string | null) ?? null, active: Boolean(row.active) };
}

export async function updateOperario(
  session: AdminSession,
  id: string,
  payload: { name?: string; rut?: string; level?: string; phone?: string; active?: boolean },
): Promise<OperarioItem> {
  const response = await fetch(`${API_BASE}/operarios/${id}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar el operario (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return { id: asString(row.id), name: asString(row.name), rut: (row.rut as string | null) ?? null, level: asString(row.level), phone: (row.phone as string | null) ?? null, active: Boolean(row.active) };
}

export async function deleteOperario(session: AdminSession, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/operarios/${id}`, {
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    throw new Error((body.message as string | undefined) ?? `No se pudo eliminar el operario (${response.status})`);
  }
}

export async function assignOperationVehicle(
  session: AdminSession,
  operationId: string,
  payload: { vehicleId: string },
): Promise<OperationItem> {
  const response = await fetch(`${API_BASE}/operations/${operationId}/assign-vehicle`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo asignar vehÃ­culo (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapOperation(row);
}

export async function assignOperationWorkers(
  session: AdminSession,
  operationId: string,
  payload: { assignments: Array<{ operarioId?: string; userId?: string; roleInOperation: string }> },
): Promise<OperationItem> {
  const response = await fetch(`${API_BASE}/operations/${operationId}/assign-workers`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo asignar personal (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapOperation(row);
}

export async function listWarehouses(session: AdminSession): Promise<WarehouseItem[]> {
  const response = await fetch(`${API_BASE}/warehouses`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar bodegas (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
    warehouseType: asString(row.warehouseType),
    isCentral: Boolean(row.isCentral),
    active: Boolean(row.active ?? true),
  }));
}

export async function createWarehouse(
  session: AdminSession,
  payload: {
    code: string;
    name: string;
    warehouseType: string;
    isCentral?: boolean;
    active?: boolean;
  },
): Promise<WarehouseItem> {
  const response = await fetch(`${API_BASE}/warehouses`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear bodega (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
    warehouseType: asString(row.warehouseType),
    isCentral: Boolean(row.isCentral),
    active: Boolean(row.active ?? true),
  };
}

export async function listInventoryItems(
  session: AdminSession,
  active?: boolean,
): Promise<InventoryItemOption[]> {
  const query = active === undefined ? "" : `?active=${active ? "true" : "false"}`;
  const response = await fetch(`${API_BASE}/inventory/items${query}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar productos (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map(mapInventoryItem);
}

export async function listInventoryCategories(session: AdminSession): Promise<InventoryCategoryItem[]> {
  const response = await fetch(`${API_BASE}/inventory/categories`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar categorÃ­as (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    id: asString(row.id),
    name: asString(row.name),
    active: Boolean(row.active ?? true),
  }));
}

export async function createInventoryCategory(
  session: AdminSession,
  payload: { name: string; active?: boolean },
): Promise<InventoryCategoryItem> {
  const response = await fetch(`${API_BASE}/inventory/categories`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear categorÃ­a (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    name: asString(row.name),
    active: Boolean(row.active ?? true),
  };
}

export async function updateInventoryCategory(
  session: AdminSession,
  id: string,
  payload: { name?: string; active?: boolean },
): Promise<InventoryCategoryItem> {
  const response = await fetch(`${API_BASE}/inventory/categories/${id}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar categorÃ­a (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    name: asString(row.name),
    active: Boolean(row.active ?? true),
  };
}

export async function createInventoryItem(
  session: AdminSession,
  payload: {
    categoryId: string;
    sku: string;
    name: string;
    description?: string;
    unit: string;
    barcode?: string;
    minimumStock: number;
    active?: boolean;
  },
): Promise<InventoryItemOption> {
  const response = await fetch(`${API_BASE}/inventory/items`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear producto (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapInventoryItem(row);
}

export async function updateInventoryItem(
  session: AdminSession,
  id: string,
  payload: {
    categoryId?: string;
    sku?: string;
    name?: string;
    description?: string;
    unit?: string;
    barcode?: string;
    minimumStock?: number;
    active?: boolean;
  },
): Promise<InventoryItemOption> {
  const response = await fetch(`${API_BASE}/inventory/items/${id}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar producto (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapInventoryItem(row);
}

export async function deactivateInventoryItem(
  session: AdminSession,
  id: string,
): Promise<InventoryItemOption> {
  const response = await fetch(`${API_BASE}/inventory/items/${id}`, {
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo desactivar producto (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapInventoryItem(row);
}

export async function listSuppliers(session: AdminSession): Promise<SupplierItem[]> {
  const response = await fetch(`${API_BASE}/suppliers`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar proveedores (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map(mapSupplier);
}

export async function createSupplier(
  session: AdminSession,
  payload: { rut: string; name: string; email?: string; phone?: string; address?: string; active?: boolean },
): Promise<SupplierItem> {
  const response = await fetch(`${API_BASE}/suppliers`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear proveedor (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapSupplier(row);
}

export async function updateSupplier(
  session: AdminSession,
  id: string,
  payload: { rut?: string; name?: string; email?: string; phone?: string; address?: string; active?: boolean },
): Promise<SupplierItem> {
  const response = await fetch(`${API_BASE}/suppliers/${id}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar proveedor (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapSupplier(row);
}

function mapSupplier(row: Record<string, unknown>): SupplierItem {
  return {
    id: asString(row.id),
    rut: asString(row.rut),
    name: asString(row.name),
    email: asString(row.email),
    phone: asString(row.phone),
    address: asString(row.address),
    active: Boolean(row.active ?? true),
  };
}

export async function listStockBalances(
  session: AdminSession,
  warehouseId?: string,
): Promise<InventoryStockBalanceItem[]> {
  const query = warehouseId ? `?warehouseId=${encodeURIComponent(warehouseId)}` : "";
  const response = await fetch(`${API_BASE}/inventory/stock-balances${query}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar saldos de stock (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => {
    const warehouse = (row.warehouse as Record<string, unknown> | undefined) ?? {};
    const item = (row.inventoryItem as Record<string, unknown> | undefined) ?? {};
    return {
      id: asString(row.id),
      warehouseId: asString(row.warehouseId),
      warehouseName: asString(warehouse.name),
      inventoryItemId: asString(row.inventoryItemId),
      itemName: asString(item.name),
      itemSku: asString(item.sku),
      quantityOnHand: Number(row.quantityOnHand ?? 0),
      quantityReserved: Number(row.quantityReserved ?? 0),
      updatedAt: asString(row.updatedAt),
    };
  });
}

export async function createStockMovement(
  session: AdminSession,
  payload: {
    warehouseId: string;
    inventoryItemId: string;
    movementType: "in" | "out" | "adjustment";
    quantity: number;
    unitCost?: number;
    referenceType: string;
    referenceId: string;
    notes?: string;
    personName?: string;
  },
): Promise<{ id: string }> {
  const response = await fetch(`${API_BASE}/inventory/stock-movements`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo registrar movimiento (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return { id: asString(row.id) };
}

export async function listStockMovements(
  session: AdminSession,
  params?: { inventoryItemId?: string; warehouseId?: string; limit?: number },
): Promise<InventoryMovementItem[]> {
  const queryParams = new URLSearchParams();
  if (params?.inventoryItemId) queryParams.set("inventoryItemId", params.inventoryItemId);
  if (params?.warehouseId) queryParams.set("warehouseId", params.warehouseId);
  if (typeof params?.limit === "number") queryParams.set("limit", String(params.limit));
  const query = queryParams.toString();
  const response = await fetch(`${API_BASE}/inventory/stock-movements${query ? `?${query}` : ""}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar movimientos (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => {
    const warehouse = (row.warehouse as Record<string, unknown> | undefined) ?? {};
    const item = (row.inventoryItem as Record<string, unknown> | undefined) ?? {};
    return {
      id: asString(row.id),
      warehouseId: asString(row.warehouseId),
      warehouseCode: asString(warehouse.code),
      warehouseName: asString(warehouse.name),
      inventoryItemId: asString(row.inventoryItemId),
      itemSku: asString(item.sku),
      itemName: asString(item.name),
      movementType: asString(row.movementType),
      quantity: Number(row.quantity ?? 0),
      unitCost: Number(row.unitCost ?? 0),
      referenceType: asString(row.referenceType),
      referenceId: asString(row.referenceId),
      notes: asString(row.notes),
      personName: asString(row.personName),
      createdBy: asString(row.createdBy),
      createdAt: asString(row.createdAt),
    };
  });
}

export async function listPurchaseOrders(
  session: AdminSession,
  status?: string,
): Promise<PurchaseOrderItem[]> {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  const response = await fetch(`${API_BASE}/inventory/purchase-orders${query}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar ordenes de compra (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => {
    const supplier = (row.supplier as Record<string, unknown> | undefined) ?? {};
    const items = ((row.items as Array<Record<string, unknown>> | undefined) ?? []).map((itemRow) => {
      const inventoryItem = (itemRow.inventoryItem as Record<string, unknown> | undefined) ?? {};
      return {
        inventoryItemId: asString(itemRow.inventoryItemId),
        itemName: asString(inventoryItem.name),
        quantity: Number(itemRow.quantity ?? 0),
        unitCost: Number(itemRow.unitCost ?? 0),
        totalCost: Number(itemRow.totalCost ?? 0),
      };
    });
    return {
      id: asString(row.id),
      orderNumber: asString(row.orderNumber),
      docType: asString(row.docType),
      docNumber: asString(row.docNumber),
      docDate: asString(row.docDate),
      status: asString(row.status),
      orderedAt: asString(row.orderedAt),
      totalAmount: Number(row.totalAmount ?? 0),
      supplierName: asString(supplier.name),
      items,
    };
  });
}

export async function createPurchaseOrder(
  session: AdminSession,
  payload: {
    supplierId: string;
    docType: "oc" | "factura" | "guia";
    docNumber: string;
    docDate: string;
    items: Array<{ inventoryItemId: string; quantity: number; unitCost: number }>;
    createdBy?: string;
  },
): Promise<{ id: string; orderNumber: string }> {
  const response = await fetch(`${API_BASE}/inventory/purchase-orders`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear orden de compra (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return { id: asString(row.id), orderNumber: asString(row.orderNumber) };
}

export async function listStockTransfers(
  session: AdminSession,
  status?: string,
): Promise<StockTransferItem[]> {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  const response = await fetch(`${API_BASE}/inventory/transfers${query}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar transferencias (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map(mapStockTransfer);
}

export async function createStockTransfer(
  session: AdminSession,
  payload: {
    fromWarehouseId: string;
    toWarehouseId: string;
    items: Array<{ inventoryItemId: string; quantity: number }>;
    createdBy?: string;
  },
): Promise<StockTransferItem> {
  const response = await fetch(`${API_BASE}/inventory/transfers`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear transferencia (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapStockTransfer(row);
}

export async function completeStockTransfer(
  session: AdminSession,
  id: string,
  payload: { completedBy?: string } = {},
): Promise<StockTransferItem> {
  const response = await fetch(`${API_BASE}/inventory/transfers/${id}/complete`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo completar transferencia (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapStockTransfer(row);
}

export async function receivePurchaseOrder(
  session: AdminSession,
  id: string,
  payload: { warehouseId: string; receivedBy?: string },
): Promise<void> {
  const response = await fetch(`${API_BASE}/inventory/purchase-orders/${id}/receive`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo recepcionar orden (${response.status})`);
}

export async function listLowStockAlerts(
  session: AdminSession,
): Promise<Array<{ inventoryItemId: string; sku: string; name: string; totalOnHand: number; minimumStock: number; deficit: number }>> {
  const response = await fetch(`${API_BASE}/inventory/alerts/low-stock`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar alertas de stock (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    inventoryItemId: asString(row.inventoryItemId),
    sku: asString(row.sku),
    name: asString(row.name),
    totalOnHand: Number(row.totalOnHand ?? 0),
    minimumStock: Number(row.minimumStock ?? 0),
    deficit: Number(row.deficit ?? 0),
  }));
}

export async function getInventoryValuation(
  session: AdminSession,
): Promise<{ totalValue: number }> {
  const response = await fetch(`${API_BASE}/inventory/reports/valuation`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar valorizaciÃ³n (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    totalValue: Number(row.totalValue ?? 0),
  };
}

export async function createStockIssueVoucher(
  session: AdminSession,
  payload: {
    operationId: string;
    warehouseId: string;
    issuedBy?: string;
    items: Array<{ inventoryItemId: string; quantity: number }>;
    notes?: string;
  },
): Promise<{ id: string; voucherNumber: string }> {
  const response = await fetch(`${API_BASE}/inventory/stock-issue-vouchers`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo emitir vale de salida (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    voucherNumber: asString(row.voucherNumber),
  };
}

function mapStockTransfer(row: Record<string, unknown>): StockTransferItem {
  const fromWarehouse = (row.fromWarehouse as Record<string, unknown> | undefined) ?? {};
  const toWarehouse = (row.toWarehouse as Record<string, unknown> | undefined) ?? {};
  const items = ((row.items as Array<Record<string, unknown>> | undefined) ?? []).map((itemRow) => {
    const inventoryItem = (itemRow.inventoryItem as Record<string, unknown> | undefined) ?? {};
    return {
      inventoryItemId: asString(itemRow.inventoryItemId),
      itemSku: asString(inventoryItem.sku),
      itemName: asString(inventoryItem.name),
      quantity: Number(itemRow.quantity ?? 0),
    };
  });

  return {
    id: asString(row.id),
    fromWarehouseId: asString(row.fromWarehouseId),
    fromWarehouseCode: asString(fromWarehouse.code),
    fromWarehouseName: asString(fromWarehouse.name),
    toWarehouseId: asString(row.toWarehouseId),
    toWarehouseCode: asString(toWarehouse.code),
    toWarehouseName: asString(toWarehouse.name),
    status: asString(row.status),
    requestedAt: asString(row.requestedAt),
    completedAt: asString(row.completedAt),
    items,
  };
}

function mapInventoryItem(row: Record<string, unknown>): InventoryItemOption {
  return {
    id: asString(row.id),
    sku: asString(row.sku),
    name: asString(row.name),
    unit: asString(row.unit),
    active: Boolean(row.active),
    categoryId: asString(row.categoryId),
    categoryName: asString(((row.category as Record<string, unknown> | undefined) ?? {}).name),
    minimumStock: Number(row.minimumStock ?? 0),
  };
}

function mapOperation(row: Record<string, unknown>): OperationItem {
  const operationType = (row.operationType as Record<string, unknown> | undefined) ?? {};
  const contract = (row.contract as Record<string, unknown> | undefined) ?? {};
  const customer = (row.customer as Record<string, unknown> | undefined) ?? {};
  const assignments = ((row.assignments as Array<Record<string, unknown>> | undefined) ?? []).map((assignment) => ({
    id: asString(assignment.id),
    operarioId: (assignment.operarioId as string | null) ?? null,
    userId: (assignment.userId as string | null) ?? null,
    roleInOperation: asString(assignment.roleInOperation),
  }));
  const vehicles = ((row.vehicles as Array<Record<string, unknown>> | undefined) ?? []).map((entry) => {
    const vehicle = (entry.vehicle as Record<string, unknown> | undefined) ?? {};
    return {
      id: asString(entry.id),
      vehicleId: asString(entry.vehicleId),
      code: asString(vehicle.code),
      name: asString(vehicle.name),
      plate: asString(vehicle.plate),
      status: asString(vehicle.status),
    };
  });
  const materials = ((row.materials as Array<Record<string, unknown>> | undefined) ?? []).map((material) => ({
    id: asString(material.id),
    inventoryItemId: asString(material.inventoryItemId),
    materialName: asString(material.materialName),
    quantityRequested: Number(material.quantityRequested ?? 0),
    quantityUsed: Number(material.quantityUsed ?? 0),
  }));
  return {
    id: asString(row.id),
    sequenceNumber: Number(row.sequenceNumber ?? 0),
    contractId: asString(row.contractId),
    customerId: asString(row.customerId),
    operationTypeId: asString(row.operationTypeId),
    source: asString(row.source),
    status: asString(row.status),
    priority: asString(row.priority),
    requestSummary: asString(row.requestSummary),
    diagnosis: asString(row.diagnosis),
    resolution: asString(row.resolution),
    scheduledAt: asString(row.scheduledAt),
    startedAt: asString(row.startedAt),
    completedAt: asString(row.completedAt),
    archivedAt: (row.archivedAt as string | null) ?? null,
    requestedAt: asString(row.requestedAt),
    operationTypeName: asString(operationType.name),
    operationTypeCategory: asString(operationType.category),
    contractNumber: asString(contract.contractNumber),
    contractAddress: asString(contract.startAddress),
    customerName:
      asString(customer.fullName) ||
      asString(customer.businessName) ||
      [asString(customer.firstName), asString(customer.middleName), asString(customer.lastName)]
        .filter(Boolean)
        .join(" ")
        .trim(),
    assignments,
    vehicles,
    materials,
  };
}

export async function listSectors(session: AdminSession): Promise<SectorItem[]> {
  const response = await fetch(`${API_BASE}/sectors`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar sectores (${response.status})`);
  const data = (await response.json()) as Array<Record<string, unknown>>;
  return data.map((row) => ({
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
  }));
}

export async function createSector(
  session: AdminSession,
  payload: { code: string; name: string },
): Promise<SectorItem> {
  const response = await fetch(`${API_BASE}/sectors`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear sector (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
  };
}

export async function updateSector(
  session: AdminSession,
  id: string,
  payload: { code: string; name: string },
): Promise<SectorItem> {
  const response = await fetch(`${API_BASE}/sectors/${id}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar sector (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
  };
}

export async function deleteSector(session: AdminSession, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/sectors/${id}`, {
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo eliminar sector (${response.status})`);
}

export async function listReadingRoutes(
  session: AdminSession,
  options?: { sectorId?: string },
): Promise<ReadingRouteItem[]> {
  const query = options?.sectorId ? `?sectorId=${encodeURIComponent(options.sectorId)}` : "";
  const response = await fetch(`${API_BASE}/reading-routes${query}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar rutas (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
    active: Boolean(row.active),
    sectorIds: ((row.sectorIdsJson as string[] | undefined) ?? []).map((value) => asString(value)).filter(Boolean),
    contractIds: ((row.contractIdsJson as string[] | undefined) ?? []).map((value) => asString(value)).filter(Boolean),
  }));
}

export async function createReadingRoute(
  session: AdminSession,
  payload: { code: string; name: string; sectorIdsJson: string[]; contractIdsJson: string[]; active?: boolean },
): Promise<ReadingRouteItem> {
  const response = await fetch(`${API_BASE}/reading-routes`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear ruta (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
    active: Boolean(row.active),
    sectorIds: ((row.sectorIdsJson as string[] | undefined) ?? []).map((value) => asString(value)).filter(Boolean),
    contractIds: ((row.contractIdsJson as string[] | undefined) ?? []).map((value) => asString(value)).filter(Boolean),
  };
}

export async function updateReadingRoute(
  session: AdminSession,
  id: string,
  payload: { code: string; name: string; sectorIdsJson: string[]; contractIdsJson: string[]; active: boolean },
): Promise<ReadingRouteItem> {
  const response = await fetch(`${API_BASE}/reading-routes/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar ruta (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
    active: Boolean(row.active),
    sectorIds: ((row.sectorIdsJson as string[] | undefined) ?? []).map((value) => asString(value)).filter(Boolean),
    contractIds: ((row.contractIdsJson as string[] | undefined) ?? []).map((value) => asString(value)).filter(Boolean),
  };
}

export async function deleteReadingRoute(session: AdminSession, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/reading-routes/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo eliminar ruta (${response.status})`);
}

export async function listMobileDevices(session: AdminSession): Promise<MobileDeviceItem[]> {
  const response = await fetch(`${API_BASE}/mobile/devices`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar dispositivos (${response.status})`);
  const data = (await response.json()) as Array<Record<string, unknown>>;
  return data.map((row) => ({
    id: asString(row.id),
    deviceCode: asString(row.deviceCode),
    name: asString(row.name),
    status: asString(row.status),
    lastSyncAt: asString(row.lastSyncAt),
  }));
}

export async function createMobileDevice(
  session: AdminSession,
  payload: { deviceCode: string; name: string; status?: string },
): Promise<MobileDeviceItem> {
  const response = await fetch(`${API_BASE}/mobile/devices`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear dispositivo (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    deviceCode: asString(row.deviceCode),
    name: asString(row.name),
    status: asString(row.status),
    lastSyncAt: asString(row.lastSyncAt),
  };
}

export async function updateMobileDevice(
  session: AdminSession,
  id: string,
  payload: { deviceCode: string; name: string; status: string },
): Promise<MobileDeviceItem> {
  const response = await fetch(`${API_BASE}/mobile/devices/${id}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar dispositivo (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    deviceCode: asString(row.deviceCode),
    name: asString(row.name),
    status: asString(row.status),
    lastSyncAt: asString(row.lastSyncAt),
  };
}

export async function deleteMobileDevice(session: AdminSession, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/mobile/devices/${id}`, {
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo eliminar dispositivo (${response.status})`);
}

export async function listReadingPeriods(session: AdminSession): Promise<ReadingPeriodItem[]> {
  const response = await fetch(`${API_BASE}/reading-periods`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar periodos de lectura (${response.status})`);
  const data = (await response.json()) as Array<Record<string, unknown>>;
  return data.map((row) => ({
    id: asString(row.id),
    periodCode: asString(row.periodCode),
    status: asString(row.status),
  }));
}

export type BulkImportReadingRow = {
  contractNumber: string;
  periodCode: string;
  previousReading: number;
  currentReading: number;
  readAt?: string;
};

export type BulkImportReadingsResult = {
  imported: number;
  updated: number;
  skipped: number;
  errors: string[];
};

export async function bulkImportReadings(
  session: AdminSession,
  rows: BulkImportReadingRow[],
): Promise<BulkImportReadingsResult> {
  const response = await fetch(`${API_BASE}/meter-readings/bulk-import`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify({ rows }),
  });
  if (!response.ok) {
    let detail = "";
    try {
      const body = (await response.json()) as { message?: string | string[] };
      detail = Array.isArray(body.message) ? body.message.join(", ") : (body.message ?? "");
    } catch {
      // noop
    }
    throw new Error(detail || `Error al importar (${response.status})`);
  }
  return (await response.json()) as BulkImportReadingsResult;
}

export async function listExpenseConcepts(session: AdminSession): Promise<ExpenseConceptItem[]> {
  const response = await fetch(`${API_BASE}/expense-concepts`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar conceptos de gasto (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => mapExpenseConcept(row));
}

export async function createExpenseConcept(
  session: AdminSession,
  payload: CreateExpenseConceptPayload,
): Promise<ExpenseConceptItem> {
  const response = await fetch(`${API_BASE}/expense-concepts`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear concepto de gasto (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapExpenseConcept(row);
}

export async function updateExpenseConcept(
  session: AdminSession,
  id: string,
  payload: UpdateExpenseConceptPayload,
): Promise<ExpenseConceptItem> {
  const response = await fetch(`${API_BASE}/expense-concepts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar concepto de gasto (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapExpenseConcept(row);
}

export async function deleteExpenseConcept(session: AdminSession, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/expense-concepts/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo eliminar concepto de gasto (${response.status})`);
}

export async function closeReadingPeriod(
  session: AdminSession,
  periodId: string,
  payload?: { closedBy?: string },
): Promise<void> {
  const response = await fetch(`${API_BASE}/reading-periods/${encodeURIComponent(periodId)}/close`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload ?? {}),
  });
  if (!response.ok) throw new Error(`No se pudo cerrar el perÃ­odo (${response.status})`);
}

export async function listReadingAssignments(
  session: AdminSession,
  readingPeriodId?: string,
): Promise<ReadingAssignmentItem[]> {
  const query = readingPeriodId ? `?readingPeriodId=${encodeURIComponent(readingPeriodId)}` : "";
  const response = await fetch(`${API_BASE}/reading-assignments${query}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar asignaciones (${response.status})`);
  const data = (await response.json()) as Array<Record<string, unknown>>;
  return data.map((row) => {
    const sector = (row.sector as Record<string, unknown> | undefined) ?? {};
    const readingPeriod = (row.readingPeriod as Record<string, unknown> | undefined) ?? {};
    return {
      id: asString(row.id),
      readingPeriodId: asString(row.readingPeriodId),
      sectorId: asString(row.sectorId),
      routeId: asString(row.routeId),
      assignedDeviceCode: asString(row.assignedDeviceCode),
      assignmentStatus: asString(row.assignmentStatus),
      sectorName: asString(sector.name),
      routeName: asString(((row.route as Record<string, unknown> | undefined) ?? {}).name),
      periodCode: asString(readingPeriod.periodCode),
    };
  });
}

export async function deleteReadingAssignment(session: AdminSession, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/reading-assignments/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo eliminar asignacion (${response.status})`);
}

export async function createReadingAssignment(
  session: AdminSession,
  payload: {
    readingPeriodId: string;
    sectorId?: string;
    routeId?: string;
    assignedDeviceCode: string;
    assignmentStatus?: string;
  },
): Promise<ReadingAssignmentItem> {
  const response = await fetch(`${API_BASE}/reading-assignments`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear asignacion (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  const sector = (row.sector as Record<string, unknown> | undefined) ?? {};
  const readingPeriod = (row.readingPeriod as Record<string, unknown> | undefined) ?? {};
  return {
    id: asString(row.id),
    readingPeriodId: asString(row.readingPeriodId),
    sectorId: asString(row.sectorId),
    routeId: asString(row.routeId),
    assignedDeviceCode: asString(row.assignedDeviceCode),
    assignmentStatus: asString(row.assignmentStatus),
    sectorName: asString(sector.name),
    routeName: asString(((row.route as Record<string, unknown> | undefined) ?? {}).name),
    periodCode: asString(readingPeriod.periodCode),
  };
}

export async function runBillingPeriodClosure(
  session: AdminSession,
  payload: {
    readingPeriodId: string;
    forceWithoutReading?: boolean;
    selectionMode?: "document_type" | "contract";
    documentTypes?: Array<"33" | "34" | "39" | "41">;
    contractIds?: string[];
  },
): Promise<{ closureId: string; documentsCreated: number }> {
  const response = await fetch(`${API_BASE}/billing/period-closures/run`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let reason = "";
    try {
      const body = (await response.json()) as { message?: string | string[] };
      if (Array.isArray(body.message)) reason = body.message.join(". ");
      else reason = body.message ?? "";
    } catch {
      reason = "";
    }
    throw new Error(
      reason ? `No se pudo ejecutar cierre de periodo (${response.status}): ${reason}` : `No se pudo ejecutar cierre de periodo (${response.status})`,
    );
  }
  const row = (await response.json()) as Record<string, unknown>;
  return {
    closureId: asString(row.closureId),
    documentsCreated: Number(row.documentsCreated ?? 0),
  };
}

export async function getBillingClosurePreview(
  session: AdminSession,
  readingPeriodId: string,
  documentTypes?: Array<"33" | "34" | "39" | "41">,
): Promise<BillingClosurePreviewItem[]> {
  const query = new URLSearchParams({ readingPeriodId });
  if (documentTypes && documentTypes.length > 0) query.set("documentTypes", documentTypes.join(","));
  const response = await fetch(`${API_BASE}/billing/period-closures/preview?${query.toString()}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar preview de facturacion (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    contractId: asString(row.contractId),
    contractNumber: asString(row.contractNumber),
    customerName: asString(row.customerName),
    customerRut: asString(row.customerRut),
    customerAddress: asString(row.customerAddress),
    customerEmail: asString(row.customerEmail),
    contractAddress: asString(row.contractAddress),
    commune: asString(row.commune),
    sector: asString(row.sector),
    serviceNumber: asString(row.serviceNumber),
    tariffName: asString(row.tariffName),
    documentType: normalizeBillingDocumentType(asString(row.documentType)),
    billedInPeriod: Number(row.billedInPeriod ?? 0),
    consumptionM3: Number(row.consumptionM3 ?? 0),
    fixedCharge: Number(row.fixedCharge ?? 0),
    variableChargesTotal: Number(row.variableChargesTotal ?? 0),
    previousBalance: Number(row.previousBalance ?? 0),
    netAmount: Number(row.netAmount ?? 0),
    taxAmount: Number(row.taxAmount ?? 0),
    totalAmount: Number(row.totalAmount ?? row.updatedTotalAmount ?? 0),
    currentConsumptionCharge: Number(row.currentConsumptionCharge ?? 0),
    updatedConsumptionCharge: Number(row.updatedConsumptionCharge ?? 0),
    updatedTotalAmount: Number(row.updatedTotalAmount ?? 0),
    differenceAmount: Number(row.differenceAmount ?? 0),
    items: Array.isArray(row.items)
      ? row.items.map((item) => {
          const entry = item as Record<string, unknown>;
          return {
            description: asString(entry.description),
            quantity: Number(entry.quantity ?? 0),
            unitPrice: Number(entry.unitPrice ?? 0),
            totalPrice: Number(entry.totalPrice ?? 0),
          };
        })
      : [],
    consumptionBreakdown: Array.isArray(row.consumptionBreakdown)
      ? row.consumptionBreakdown.map((item) => {
          const entry = item as Record<string, unknown>;
          return {
            sortOrder: Number(entry.sortOrder ?? 0),
            billedM3: Number(entry.billedM3 ?? 0),
            unitPrice: Number(entry.unitPrice ?? 0),
            subtotal: Number(entry.subtotal ?? 0),
          };
        })
      : [],
  }));
}

export async function getSettingsSnapshot(session: AdminSession): Promise<SettingsSnapshot> {
  const response = await fetch(`${API_BASE}/settings`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar configuraciones (${response.status})`);
  return (await response.json()) as SettingsSnapshot;
}

export async function getNextContractNumber(
  session: AdminSession,
): Promise<{ nextNumber: number; step: number; seed: number }> {
  const response = await fetch(`${API_BASE}/settings/contracts/next-number`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo obtener correlativo (${response.status})`);
  return (await response.json()) as { nextNumber: number; step: number; seed: number };
}

export async function listSystemRoles(session: AdminSession): Promise<SystemRoleItem[]> {
  const response = await fetch(`${API_BASE}/settings/roles`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar roles (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
    description: asString(row.description),
    modules: ((row.modules as string[] | undefined) ?? []).map((module) => asString(module)).filter(Boolean),
  }));
}

export async function updateSystemRolePermissions(
  session: AdminSession,
  roleCode: string,
  modules: string[],
): Promise<void> {
  const response = await fetch(`${API_BASE}/settings/roles/${encodeURIComponent(roleCode)}/modules`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify({ modules }),
  });
  if (!response.ok) throw new Error(`No se pudieron actualizar permisos (${response.status})`);
}

export async function listSystemUsers(session: AdminSession): Promise<SystemUserItem[]> {
  const response = await fetch(`${API_BASE}/settings/users`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar usuarios (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => {
    const role = (row.role as Record<string, unknown> | undefined) ?? {};
    const status = asString(row.status) === "inactive" ? "inactive" : "active";
    return {
      id: asString(row.id),
      fullName: asString(row.fullName),
      email: asString(row.email),
      status,
      lastLoginAt: asString(row.lastLoginAt) || null,
      role: asString(role.code)
        ? {
            code: asString(role.code),
            name: asString(role.name),
          }
        : null,
    };
  });
}

export type CutEligibleItem = {
  contractId: string;
  contractNumber: string;
  customerId: string;
  customerName: string;
  customerRut: string;
  customerStatus: string;
  address: string;
  sector: string;
  monthsOverdue: number;
  daysOverdue: number;
  totalOutstanding: number;
  overdueDocuments: number;
  contractStatus: string;
};

export type CutAppliedItem = {
  contractId: string;
  contractNumber: string;
  customerId: string;
  customerName: string;
  customerRut: string;
  customerStatus: string;
  address: string;
  sector: string;
  totalOutstanding: number;
  cutDate: string | null;
  contractStatus: string;
};

export async function getCutsEligible(
  session: AdminSession,
  params: { minMonths?: number; sectorName?: string; minAmount?: number },
): Promise<CutEligibleItem[]> {
  const qs = new URLSearchParams();
  if (params.minMonths !== undefined) qs.set("minMonths", String(params.minMonths));
  if (params.sectorName) qs.set("sectorName", params.sectorName);
  if (params.minAmount !== undefined) qs.set("minAmount", String(params.minAmount));
  const response = await fetch(`${API_BASE}/collections/cuts/eligible?${qs.toString()}`, {
    headers: { authorization: `Bearer ${session.accessToken}`, "x-tenant-id": session.tenantId },
  });
  if (!response.ok) throw new Error(`No se pudo cargar elegibles para corte (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    contractId: asString(row.contractId),
    contractNumber: asString(row.contractNumber),
    customerId: asString(row.customerId),
    customerName: asString(row.customerName),
    customerRut: asString(row.customerRut),
    customerStatus: asString(row.customerStatus),
    address: asString(row.address),
    sector: asString(row.sector),
    monthsOverdue: Number(row.monthsOverdue ?? 0),
    daysOverdue: Number(row.daysOverdue ?? 0),
    totalOutstanding: Number(row.totalOutstanding ?? 0),
    overdueDocuments: Number(row.overdueDocuments ?? 0),
    contractStatus: asString(row.contractStatus),
  }));
}

export async function applyCuts(
  session: AdminSession,
  contractIds: string[],
  reason?: string,
): Promise<{ applied: number; errors: string[] }> {
  const response = await fetch(`${API_BASE}/collections/cuts/apply`, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${session.accessToken}`, "x-tenant-id": session.tenantId },
    body: JSON.stringify({ contractIds, reason }),
  });
  if (!response.ok) throw new Error(`No se pudo aplicar cortes (${response.status})`);
  return (await response.json()) as { applied: number; errors: string[] };
}

export async function restoreCuts(
  session: AdminSession,
  contractIds: string[],
  reason?: string,
): Promise<{ restored: number; errors: string[] }> {
  const response = await fetch(`${API_BASE}/collections/cuts/restore`, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${session.accessToken}`, "x-tenant-id": session.tenantId },
    body: JSON.stringify({ contractIds, reason }),
  });
  if (!response.ok) throw new Error(`No se pudo restaurar cortes (${response.status})`);
  return (await response.json()) as { restored: number; errors: string[] };
}

export async function listCuts(session: AdminSession): Promise<CutAppliedItem[]> {
  const response = await fetch(`${API_BASE}/collections/cuts`, {
    headers: { authorization: `Bearer ${session.accessToken}`, "x-tenant-id": session.tenantId },
  });
  if (!response.ok) throw new Error(`No se pudo cargar cortes (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    contractId: asString(row.contractId),
    contractNumber: asString(row.contractNumber),
    customerId: asString(row.customerId),
    customerName: asString(row.customerName),
    customerRut: asString(row.customerRut),
    customerStatus: asString(row.customerStatus),
    address: asString(row.address),
    sector: asString(row.sector),
    totalOutstanding: Number(row.totalOutstanding ?? 0),
    cutDate: row.cutDate ? asString(row.cutDate) : null,
    contractStatus: asString(row.contractStatus),
  }));
}

export async function listSecurityAuditLogs(
  session: AdminSession,
  limit = 100,
): Promise<SecurityAuditLogItem[]> {
  const safeLimit = Number.isFinite(limit) ? Math.max(1, Math.min(300, Math.trunc(limit))) : 100;
  const response = await fetch(`${API_BASE}/settings/audit-logs?limit=${encodeURIComponent(String(safeLimit))}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar auditorÃ­a (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    id: asString(row.id),
    module: asString(row.module),
    action: asString(row.action),
    entityName: asString(row.entityName),
    entityId: asString(row.entityId) || null,
    userName: asString(row.userName) || "Sistema",
    userEmail: asString(row.userEmail) || null,
    createdAt: asString(row.createdAt),
  }));
}

export async function createSystemUser(
  session: AdminSession,
  payload: { fullName: string; email: string; roleCode: string; password?: string },
): Promise<{ temporaryPassword: string | null }> {
  const response = await fetch(`${API_BASE}/settings/users`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear usuario (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    temporaryPassword: asString(row.temporaryPassword) || null,
  };
}

export async function updateSystemUserStatus(
  session: AdminSession,
  userId: string,
  status: "active" | "inactive",
): Promise<void> {
  const response = await fetch(`${API_BASE}/settings/users/${userId}/status`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify({ status }),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar estado de usuario (${response.status})`);
}

export async function updateSystemUserRole(
  session: AdminSession,
  userId: string,
  roleCode: string,
): Promise<void> {
  const response = await fetch(`${API_BASE}/settings/users/${userId}/role`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify({ roleCode }),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar rol de usuario (${response.status})`);
}

export async function updateSystemUser(
  session: AdminSession,
  userId: string,
  payload: { fullName?: string; email?: string; password?: string },
): Promise<SystemUserItem> {
  const response = await fetch(`${API_BASE}/settings/users/${userId}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar usuario (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  const role = (row.role as Record<string, unknown> | undefined) ?? {};
  const status = asString(row.status) === "inactive" ? "inactive" : "active";
  return {
    id: asString(row.id),
    fullName: asString(row.fullName),
    email: asString(row.email),
    status,
    lastLoginAt: asString(row.lastLoginAt) || null,
    role: asString(role.code)
      ? {
          code: asString(role.code),
          name: asString(role.name),
        }
      : null,
  };
}

export async function updateOrganizationSettings(
  session: AdminSession,
  payload: {
    companyName?: string;
    companyRut?: string;
    giro?: string;
    region?: string;
    comuna?: string;
    address?: string;
    phone?: string;
    email?: string;
    contractNumberSeed?: number;
    contractNumberStep?: number;
    morosidadMonths?: number;
  },
): Promise<void> {
  const response = await fetch(`${API_BASE}/settings/organization`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar configuraciÃ³n de organizaciÃ³n (${response.status})`);
}

export async function updateBillingSettings(
  session: AdminSession,
  payload: {
    billingFrequency?: string;
    cutoffDay?: number;
    dueDay?: number;
  },
): Promise<void> {
  const response = await fetch(`${API_BASE}/settings/billing`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar configuraciÃ³n de facturaciÃ³n (${response.status})`);
}

export async function updateInvoiceIntegrationSettings(
  session: AdminSession,
  payload: {
    providerName?: string;
    endpoint?: string;
    status?: string;
    extraJson?: Record<string, unknown>;
  },
): Promise<void> {
  const response = await fetch(`${API_BASE}/settings/invoice-integration`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar integraciÃ³n de facturaciÃ³n (${response.status})`);
}

export async function updateSecuritySettings(
  session: AdminSession,
  payload: {
    sessionTimeoutMinutes?: number;
    passwordMinLength?: number;
    passwordRotationDays?: number;
    mfaForAdmins?: boolean;
    lockoutAttempts?: number;
    lockoutMinutes?: number;
    alertChannelsJson?: Record<string, { email?: boolean; sms?: boolean; sistema?: boolean }>;
    delinquencyGraceDays?: number;
    abnormalConsumptionThresholdPercent?: number;
  },
): Promise<void> {
  const response = await fetch(`${API_BASE}/settings/security`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar configuracion de seguridad (${response.status})`);
}

export async function getBillingFolioAvailability(
  session: AdminSession,
  readingPeriodId: string,
  forceWithoutReading = false,
): Promise<FolioAvailabilitySummary> {
  const query = new URLSearchParams({ readingPeriodId });
  if (forceWithoutReading) query.set("forceWithoutReading", "true");
  const response = await fetch(`${API_BASE}/billing/folios/availability?${query.toString()}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo consultar folios (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    readingPeriodId: asString(row.readingPeriodId),
    forceWithoutReading: Boolean(row.forceWithoutReading),
    requiredTotal: Number(row.requiredTotal ?? 0),
    items: ((row.items as Array<Record<string, unknown>> | undefined) ?? []).map((item) => ({
      type: Number(item.type ?? 0),
      required: Number(item.required ?? 0),
      available: Number(item.available ?? 0),
      folioActual: item.folioActual == null ? null : Number(item.folioActual),
      folioFinal: item.folioFinal == null ? null : Number(item.folioFinal),
      ok: Boolean(item.ok),
      status: asString(item.status),
    })),
  };
}

export async function listPeriodVariableCharges(
  session: AdminSession,
  readingPeriodId: string,
  q?: string,
): Promise<PeriodVariableChargeItem[]> {
  const query = new URLSearchParams({ readingPeriodId });
  if (q?.trim()) query.set("q", q.trim());
  const response = await fetch(`${API_BASE}/billing/period-variable-charges?${query.toString()}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar gastos variables (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => mapPeriodVariableCharge(row));
}

export async function createPeriodVariableCharges(
  session: AdminSession,
  payload: CreatePeriodVariableChargesPayload,
): Promise<PeriodVariableChargeItem[]> {
  const response = await fetch(`${API_BASE}/billing/period-variable-charges`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudieron crear gastos variables (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => mapPeriodVariableCharge(row));
}

export async function deletePeriodVariableCharge(session: AdminSession, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/billing/period-variable-charges/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo eliminar gasto variable (${response.status})`);
}

export async function processPendingInvoiceJobs(
  session: AdminSession,
  limit = 200,
  documentTypes?: Array<"33" | "34" | "39" | "41">,
  contractIds?: string[],
): Promise<{ requested: number; processed: number; failed: number; pendingSii: number; hasMore: boolean; remainingPending: number; documentIds: string[] }> {
  const query = new URLSearchParams({ limit: String(limit) });
  if (documentTypes && documentTypes.length > 0) query.set("documentTypes", documentTypes.join(","));
  if (contractIds && contractIds.length > 0) query.set("contractIds", contractIds.join(","));
  const response = await fetch(`${API_BASE}/billing/invoice-jobs/process-pending?${query.toString()}`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) {
    throw new Error(`No se pudieron procesar jobs (${response.status})`);
  }
  const row = (await response.json()) as Record<string, unknown>;
  return {
    requested: Number(row.requested ?? 0),
    processed: Number(row.processed ?? 0),
    failed: Number(row.failed ?? 0),
    pendingSii: Number(row.pendingSii ?? 0),
    hasMore: Boolean(row.hasMore),
    remainingPending: Number(row.remainingPending ?? 0),
    documentIds: Array.isArray(row.documentIds) ? row.documentIds.map((id) => asString(id)).filter(Boolean) : [],
  };
}

export type InvoiceProcessingRunPhase = "emitting" | "validating" | "done" | "failed";

/** Avance de la corrida unica de emision+validacion (POST/GET /billing/invoice-jobs/process-all). */
export type InvoiceProcessingRun = {
  id: string;
  phase: InvoiceProcessingRunPhase;
  startedAt: string;
  finishedAt: string | null;
  total: number;
  attempted: number;
  issued: number;
  failed: number;
  pendingSii: number;
  remainingPending: number;
  validationRounds: number;
  validationDeadline: string | null;
  documentIds: string[];
  message: string;
  error: string | null;
};

function mapInvoiceProcessingRun(row: Record<string, unknown>): InvoiceProcessingRun {
  const phase = asString(row.phase);
  return {
    id: asString(row.id),
    phase: phase === "validating" || phase === "done" || phase === "failed" ? phase : "emitting",
    startedAt: asString(row.startedAt),
    finishedAt: row.finishedAt ? asString(row.finishedAt) : null,
    total: Number(row.total ?? 0),
    attempted: Number(row.attempted ?? 0),
    issued: Number(row.issued ?? 0),
    failed: Number(row.failed ?? 0),
    pendingSii: Number(row.pendingSii ?? 0),
    remainingPending: Number(row.remainingPending ?? 0),
    validationRounds: Number(row.validationRounds ?? 0),
    validationDeadline: row.validationDeadline ? asString(row.validationDeadline) : null,
    documentIds: Array.isArray(row.documentIds) ? row.documentIds.map((id) => asString(id)).filter(Boolean) : [],
    message: asString(row.message),
    error: row.error ? asString(row.error) : null,
  };
}

/** Lanza (o retoma, si ya hay una activa) la corrida que emite y valida todos los pendientes. */
export async function startInvoiceProcessingRun(
  session: AdminSession,
  documentTypes?: Array<"33" | "34" | "39" | "41">,
  contractIds?: string[],
): Promise<InvoiceProcessingRun> {
  const query = new URLSearchParams();
  if (documentTypes && documentTypes.length > 0) query.set("documentTypes", documentTypes.join(","));
  if (contractIds && contractIds.length > 0) query.set("contractIds", contractIds.join(","));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await fetch(`${API_BASE}/billing/invoice-jobs/process-all${suffix}`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) {
    throw new Error(`No se pudo iniciar el proceso de facturacion (${response.status})`);
  }
  return mapInvoiceProcessingRun((await response.json()) as Record<string, unknown>);
}

export async function getInvoiceProcessingRun(session: AdminSession): Promise<InvoiceProcessingRun | null> {
  const response = await fetch(`${API_BASE}/billing/invoice-jobs/process-all`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) {
    throw new Error(`No se pudo consultar el proceso de facturacion (${response.status})`);
  }
  const text = await response.text();
  if (!text.trim()) return null;
  const row = JSON.parse(text) as Record<string, unknown> | null;
  return row ? mapInvoiceProcessingRun(row) : null;
}

export async function listBillingDocuments(
  session: AdminSession,
  readingPeriodId?: string,
  contractId?: string,
): Promise<BillingDocumentItem[]> {
  const queryParams = new URLSearchParams();
  if (readingPeriodId) queryParams.set("readingPeriodId", readingPeriodId);
  if (contractId) queryParams.set("contractId", contractId);
  const query = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const response = await fetch(`${API_BASE}/billing/documents${query}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar documentos (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => {
    const jobs = ((row.invoiceJobs as Array<Record<string, unknown>> | undefined) ?? []).slice().sort((a, b) =>
      asString(b.updatedAt).localeCompare(asString(a.updatedAt)),
    );
    const lastJob = jobs[0];
    const customer = (row.customer as Record<string, unknown> | undefined) ?? {};
    const contract = (row.contract as Record<string, unknown> | undefined) ?? {};
    const billingProfile = (contract.billingProfile as Record<string, unknown> | undefined) ?? null;
    const responseJson = (row.responseJson as Record<string, unknown> | undefined) ?? {};
    const recipientName = billingProfile?.recipientName ? asString(billingProfile.recipientName) : asString(customer.fullName);
    return {
      id: asString(row.id),
      readingPeriodId: asString(row.readingPeriodId),
      contractId: asString(row.contractId),
      contractNumber: asString(contract.contractNumber),
      contractAddress: asString(contract.startAddress),
      customerName: recipientName,
      documentType: asString(row.documentType),
      issueDate: asString(row.issueDate),
      dueDate: asString(row.dueDate),
      folio: asString(row.folio),
      totalAmount: Number(row.totalAmount ?? 0),
      status: asString(row.status),
      cancellationStatus: asString(row.cancellationStatus) || "none",
      externalError: asString(responseJson.reasonCode) === "ALREADY_ISSUED" ? "" : asString(responseJson.reasonMessage),
      lastJobId: asString(lastJob?.id),
      lastJobStatus: asString(lastJob?.status),
      attempts: Number(lastJob?.attempts ?? 0),
    };
  });
}

export async function listInvoiceJobs(
  session: AdminSession,
  status?: string,
): Promise<InvoiceJobItem[]> {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  const response = await fetch(`${API_BASE}/billing/invoice-jobs${query}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar jobs (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    id: asString(row.id),
    billingDocumentId: asString(row.billingDocumentId),
    providerName: asString(row.providerName),
    status: asString(row.status),
    attempts: Number(row.attempts ?? 0),
    lastError: asString(row.lastError),
  }));
}

export async function getBillingDocumentFileUrl(
  session: AdminSession,
  documentId: string,
  kind: "pdf" | "xml",
): Promise<{ url: string; path: string; filename: string }> {
  const response = await fetch(`${API_BASE}/billing/documents/${encodeURIComponent(documentId)}/file-url?kind=${encodeURIComponent(kind)}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw await errorDeApi(response, "No se pudo obtener el archivo");
  return (await response.json()) as { url: string; path: string; filename: string };
}

export async function listBillingCancellationEligibleDocuments(
  session: AdminSession,
  input?: { q?: string; contractId?: string; documentType?: string; from?: string; to?: string },
): Promise<BillingCancellationEligibleDocument[]> {
  const query = new URLSearchParams();
  if (input?.q?.trim()) query.set("q", input.q.trim());
  if (input?.contractId?.trim()) query.set("contractId", input.contractId.trim());
  if (input?.documentType?.trim()) query.set("documentType", input.documentType.trim());
  if (input?.from?.trim()) query.set("from", input.from.trim());
  if (input?.to?.trim()) query.set("to", input.to.trim());
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await fetch(`${API_BASE}/billing/cancellations/eligible-documents${suffix}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar documentos anulables (${response.status})`);
  return (await response.json()) as BillingCancellationEligibleDocument[];
}

export async function getBillingCancellationPreview(
  session: AdminSession,
  billingDocumentId: string,
  detail?: string,
): Promise<BillingCancellationPreview> {
  const response = await fetch(`${API_BASE}/billing/cancellations/preview-normal`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
      "content-type": "application/json",
    },
    body: JSON.stringify({
      billingDocumentId,
      detail,
    }),
  });
  if (!response.ok) throw new Error(`No se pudo cargar vista previa (${response.status})`);
  return (await response.json()) as BillingCancellationPreview;
}

export async function createBillingCancellationNormal(
  session: AdminSession,
  input: {
    billingDocumentId: string;
    referenceReasonCode: string;
    detail: string;
    cancellationDate?: string;
  },
): Promise<{
  cancellation: { id: string; creditNoteFolio: string | null };
  creditNoteDocument: { id: string; folio: string | null };
  contractBalance: { contractId: string; currentBalance: number; overdueBalance: number };
  file: { url: string; path: string; filename: string };
}> {
  const response = await fetch(`${API_BASE}/billing/cancellations/normal`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(`No se pudo generar la nota de credito (${response.status})`);
  return (await response.json()) as {
    cancellation: { id: string; creditNoteFolio: string | null };
    creditNoteDocument: { id: string; folio: string | null };
    contractBalance: { contractId: string; currentBalance: number; overdueBalance: number };
    file: { url: string; path: string; filename: string };
  };
}

export async function createBillingCancellationGlobal(
  session: AdminSession,
  input: {
    originalDocumentType: "39" | "33" | "34" | "41" | "48";
    cancellationDate: string;
    referenceReasonCode: string;
    referenceReasonText?: string;
    detail: string;
    amount: number;
    receiver?: {
      rut?: string;
      businessName?: string;
    };
  },
): Promise<{
  cancellation: { id: string; mode: string; status: string; creditNoteFolio: string | null };
  file: { url: string; path: string; filename: string };
}> {
  const response = await fetch(`${API_BASE}/billing/cancellations/global`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(`No se pudo generar la nota de credito global (${response.status})`);
  return (await response.json()) as {
    cancellation: { id: string; mode: string; status: string; creditNoteFolio: string | null };
    file: { url: string; path: string; filename: string };
  };
}

export async function listBillingCancellations(
  session: AdminSession,
  input?: { date?: string; limit?: number },
): Promise<BillingCancellationHistoryItem[]> {
  const query = new URLSearchParams();
  if (input?.date?.trim()) query.set("date", input.date.trim());
  if (Number.isFinite(input?.limit)) query.set("limit", String(input?.limit));
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await fetch(`${API_BASE}/billing/cancellations${suffix}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar historial de anulaciones (${response.status})`);
  return (await response.json()) as BillingCancellationHistoryItem[];
}

export async function getBillingCancellationFileUrl(
  session: AdminSession,
  cancellationId: string,
  kind: "pdf" | "xml",
): Promise<{ url: string; path: string; filename: string }> {
  const response = await fetch(`${API_BASE}/billing/cancellations/${encodeURIComponent(cancellationId)}/file-url?kind=${encodeURIComponent(kind)}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo obtener el archivo de anulacion (${response.status})`);
  return (await response.json()) as { url: string; path: string; filename: string };
}

export async function downloadBillingDocumentsZip(
  session: AdminSession,
  input: { readingPeriodId?: string; documentIds?: string[] },
): Promise<{ blob: Blob; filename: string }> {
  const query = new URLSearchParams();
  if (input.readingPeriodId) query.set("readingPeriodId", input.readingPeriodId);
  if (input.documentIds && input.documentIds.length > 0) query.set("documentIds", input.documentIds.join(","));
  const response = await fetch(`${API_BASE}/billing/documents/download-zip?${query.toString()}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo descargar ZIP (${response.status})`);
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = disposition.match(/filename="([^"]+)"/);
  return {
    blob: await response.blob(),
    filename: match?.[1] ?? `dte_lote_${new Date().toISOString().slice(0, 10)}.zip`,
  };
}

export async function processSelectedInvoiceJobs(
  session: AdminSession,
  jobIds: string[],
): Promise<unknown[]> {
  const response = await fetch(`${API_BASE}/billing/invoice-jobs/process-selected`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
      "content-type": "application/json",
    },
    body: JSON.stringify({ jobIds }),
  });
  if (!response.ok) throw new Error(`No se pudieron reprocesar jobs (${response.status})`);
  return (await response.json()) as unknown[];
}

export async function regenerateSelectedBillingDocuments(
  session: AdminSession,
  documentIds: string[],
): Promise<unknown[]> {
  const response = await fetch(`${API_BASE}/billing/documents/regenerate-selected`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
      "content-type": "application/json",
    },
    body: JSON.stringify({ documentIds }),
  });
  if (!response.ok) throw new Error(`No se pudieron regenerar documentos (${response.status})`);
  return (await response.json()) as unknown[];
}

export async function getBillingMetrics(
  session: AdminSession,
  readingPeriodId?: string,
): Promise<{
  totals: { documents: number; issued: number; failed: number; pending: number };
  jobs: { total: number; totalAttempts: number; retryJobs: number; retryRatePercent: number };
  performance: { avgLatencyMs: number };
}> {
  const query = readingPeriodId ? `?readingPeriodId=${encodeURIComponent(readingPeriodId)}` : "";
  const response = await fetch(`${API_BASE}/billing/metrics${query}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar metricas (${response.status})`);
  return (await response.json()) as {
    totals: { documents: number; issued: number; failed: number; pending: number };
    jobs: { total: number; totalAttempts: number; retryJobs: number; retryRatePercent: number };
    performance: { avgLatencyMs: number };
  };
}

function mapExpenseConcept(row: Record<string, unknown>): ExpenseConceptItem {
  return {
    id: asString(row.id),
    conceptType: asString(row.conceptType) === "variable" ? "variable" : "fixed",
    name: asString(row.name),
    fixedValue: row.fixedValue == null ? null : Number(row.fixedValue),
    isBillable: row.isBillable == null ? true : Boolean(row.isBillable),
    isGross: row.isGross == null ? false : Boolean(row.isGross),
    generatesDebt: row.generatesDebt == null ? true : Boolean(row.generatesDebt),
    status: asString(row.status) === "inactive" ? "inactive" : "active",
  };
}

function mapPeriodVariableCharge(row: Record<string, unknown>): PeriodVariableChargeItem {
  const contract = (row.contract as Record<string, unknown> | undefined) ?? {};
  const customer = (contract.customer as Record<string, unknown> | undefined) ?? {};
  const expenseConcept = (row.expenseConcept as Record<string, unknown> | undefined) ?? {};
  return {
    id: asString(row.id),
    readingPeriodId: asString(row.readingPeriodId),
    contractId: asString(row.contractId),
    contractNumber: asString(contract.contractNumber),
    customerName: asString(customer.fullName),
    expenseConceptId: asString(row.expenseConceptId),
    expenseConceptName: asString(expenseConcept.name),
    amount: Number(row.amount ?? 0),
    notes: asString(row.notes) || null,
  };
}

function mapReadingPeriodOverview(row: Record<string, unknown>): ReadingPeriodOverview {
  const period = row.period as Record<string, unknown> | undefined;
  const stats = row.stats as Record<string, unknown> | undefined;
  const filters = row.filters as Record<string, unknown> | undefined;
  return {
    period: period
      ? {
          id: asString(period.id),
          periodCode: asString(period.periodCode),
          status: asString(period.status),
        }
      : null,
    stats: {
      total: Number(stats?.total ?? 0),
      registered: Number(stats?.registered ?? 0),
      pending: Number(stats?.pending ?? 0),
      anomalies: Number(stats?.anomalies ?? 0),
      averageConsumptionM3: Number(stats?.averageConsumptionM3 ?? 0),
      completionPct: Number(stats?.completionPct ?? 0),
    },
    filters: {
      sectors: ((filters?.sectors as unknown[] | undefined) ?? []).map((value) => (typeof value === "string" ? value : "")).filter(Boolean),
      technicians: ((filters?.technicians as unknown[] | undefined) ?? []).map((value) => (typeof value === "string" ? value : "")).filter(Boolean),
    },
    rows: ((row.rows as Array<Record<string, unknown>> | undefined) ?? []).map((item) => ({
      rowId: asString(item.rowId),
      readingId: asString(item.readingId) || null,
      contractId: asString(item.contractId),
      contractNumber: asString(item.contractNumber),
      customerName: asString(item.customerName),
      customerRut: asString(item.customerRut),
      meterSerialNumber: asString(item.meterSerialNumber),
      sectorName: asString(item.sectorName),
      routeName: asString(item.routeName),
      previousReading: Number(item.previousReading ?? 0),
      currentReading: item.currentReading == null ? null : Number(item.currentReading),
      consumptionM3: item.consumptionM3 == null ? null : Number(item.consumptionM3),
      readAt: asString(item.readAt) || null,
      notes: asString(item.notes) || null,
      technicianName: asString(item.technicianName) || null,
      assignedDeviceCode: asString(item.assignedDeviceCode) || null,
      observation: asString(item.observation) || null,
      observationSeverity: asString(item.observationSeverity) || null,
      status:
        item.status === "registrada" || item.status === "pendiente" || item.status === "anomalia"
          ? item.status
          : "pendiente",
      address: asString(item.address),
    })),
  };
}

function mapMobileRouteSessionPoint(row: Record<string, unknown>): MobileRouteSessionPoint {
  return {
    sequence: Number(row.sequence ?? 0),
    eventType: asString(row.eventType),
    recordedAt: asString(row.recordedAt),
    lat: Number(row.lat ?? 0),
    lng: Number(row.lng ?? 0),
  };
}

function mapMobileRouteSessionsOverview(row: Record<string, unknown>): MobileRouteSessionsOverview {
  const stats = row.stats as Record<string, unknown> | undefined;
  const filters = row.filters as Record<string, unknown> | undefined;
  const period = row.period as Record<string, unknown> | undefined;
  return {
    period: period
      ? {
          id: asString(period.id),
          periodCode: asString(period.periodCode),
          status: asString(period.status),
        }
      : null,
    stats: {
      totalSessions: Number(stats?.totalSessions ?? 0),
      activeSessions: Number(stats?.activeSessions ?? 0),
      completedSessions: Number(stats?.completedSessions ?? 0),
      totalDurationSeconds: Number(stats?.totalDurationSeconds ?? 0),
      totalTrackPoints: Number(stats?.totalTrackPoints ?? 0),
      totalReadingPoints: Number(stats?.totalReadingPoints ?? 0),
    },
    filters: {
      routes: ((filters?.routes as unknown[] | undefined) ?? []).map((value) => (typeof value === "string" ? value : "")).filter(Boolean),
      operators: ((filters?.operators as unknown[] | undefined) ?? []).map((value) => (typeof value === "string" ? value : "")).filter(Boolean),
      devices: ((filters?.devices as unknown[] | undefined) ?? []).map((value) => (typeof value === "string" ? value : "")).filter(Boolean),
      statuses: ((filters?.statuses as unknown[] | undefined) ?? []).map((value) => (typeof value === "string" ? value : "")).filter(Boolean),
    },
    rows: ((row.rows as Array<Record<string, unknown>> | undefined) ?? []).map((item) => ({
      sessionId: asString(item.sessionId),
      clientSessionId: asString(item.clientSessionId) || null,
      assignmentId: asString(item.assignmentId) || null,
      routeId: asString(item.routeId) || null,
      routeName: asString(item.routeName),
      operatorName: asString(item.operatorName) || null,
      deviceCode: asString(item.deviceCode),
      deviceName: asString(item.deviceName),
      startedAt: asString(item.startedAt),
      endedAt: asString(item.endedAt) || null,
      durationSeconds: item.durationSeconds == null ? null : Number(item.durationSeconds),
      status: asString(item.status),
      source: asString(item.source),
      pointsCount: Number(item.pointsCount ?? 0),
      readingPointsCount: Number(item.readingPointsCount ?? 0),
      distanceMeters: Number(item.distanceMeters ?? 0),
      startPoint: item.startPoint ? mapMobileRouteSessionPoint(item.startPoint as Record<string, unknown>) : null,
      endPoint: item.endPoint ? mapMobileRouteSessionPoint(item.endPoint as Record<string, unknown>) : null,
      points: ((item.points as Array<Record<string, unknown>> | undefined) ?? []).map((point) => mapMobileRouteSessionPoint(point)),
    })),
  };
}

export async function exportBillingReportCsv(
  session: AdminSession,
  readingPeriodId?: string,
): Promise<{ filename: string; csv: string }> {
  const query = readingPeriodId ? `?readingPeriodId=${encodeURIComponent(readingPeriodId)}` : "";
  const response = await fetch(`${API_BASE}/billing/report${query}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo exportar reporte (${response.status})`);
  return (await response.json()) as { filename: string; csv: string };
}

export async function getSalesCollectionsReport(
  session: AdminSession,
  options?: { from?: string; to?: string },
): Promise<SalesCollectionsReport> {
  const query = new URLSearchParams();
  if (options?.from?.trim()) query.set("from", options.from.trim());
  if (options?.to?.trim()) query.set("to", options.to.trim());
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await fetch(`${API_BASE}/reports/sales-collections${suffix}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar recaudaciÃ³n (${response.status})`);
  return (await response.json()) as SalesCollectionsReport;
}

export async function getConsumptionBySectorReport(
  session: AdminSession,
  periodCode?: string,
): Promise<ConsumptionBySectorReport> {
  const query = new URLSearchParams();
  if (periodCode?.trim()) query.set("periodCode", periodCode.trim());
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await fetch(`${API_BASE}/reports/consumption-by-sector${suffix}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar consumo por sector (${response.status})`);
  return (await response.json()) as ConsumptionBySectorReport;
}

export async function getInventoryValuationReport(
  session: AdminSession,
): Promise<InventoryValuationReport> {
  const response = await fetch(`${API_BASE}/reports/inventory-valuation`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar inventario (${response.status})`);
  return (await response.json()) as InventoryValuationReport;
}

export async function getReadingsByPeriodReport(
  session: AdminSession,
  limit = 12,
): Promise<ReadingsByPeriodReport> {
  const safeLimit = Number.isFinite(limit) ? Math.max(1, Math.min(36, Math.trunc(limit))) : 12;
  const response = await fetch(`${API_BASE}/reports/readings-by-period?limit=${encodeURIComponent(String(safeLimit))}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar lecturas por perÃ­odo (${response.status})`);
  return (await response.json()) as ReadingsByPeriodReport;
}

export async function getCashMovementsReport(
  session: AdminSession,
  options?: { from?: string; to?: string },
): Promise<CashMovementsReport> {
  const query = new URLSearchParams();
  if (options?.from?.trim()) query.set("from", options.from.trim());
  if (options?.to?.trim()) query.set("to", options.to.trim());
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await fetch(`${API_BASE}/reports/cash-movements${suffix}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar caja y movimientos (${response.status})`);
  return (await response.json()) as CashMovementsReport;
}

export async function getDelinquencyReport(
  session: AdminSession,
  minDays = 1,
): Promise<DelinquencyReport> {
  const safeMinDays = Number.isFinite(minDays) ? Math.max(1, Math.trunc(minDays)) : 1;
  const response = await fetch(`${API_BASE}/reports/delinquency?minDays=${encodeURIComponent(String(safeMinDays))}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar morosidad (${response.status})`);
  return (await response.json()) as DelinquencyReport;
}

export async function getExecutiveQuarterlyReport(
  session: AdminSession,
  options?: { year?: number; quarter?: number },
): Promise<ExecutiveQuarterlyReport> {
  const query = new URLSearchParams();
  if (Number.isFinite(options?.year)) query.set("year", String(options?.year));
  if (Number.isFinite(options?.quarter)) query.set("quarter", String(options?.quarter));
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await fetch(`${API_BASE}/reports/executive-quarterly${suffix}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar resumen ejecutivo (${response.status})`);
  return (await response.json()) as ExecutiveQuarterlyReport;
}

export async function getLegalQuarterlyReport(
  session: AdminSession,
  options?: { year?: number; quarter?: number },
): Promise<LegalQuarterlyReport> {
  const query = new URLSearchParams();
  if (Number.isFinite(options?.year)) query.set("year", String(options?.year));
  if (Number.isFinite(options?.quarter)) query.set("quarter", String(options?.quarter));
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await fetch(`${API_BASE}/reports/legal-quarterly${suffix}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar informe trimestral legal (${response.status})`);
  return (await response.json()) as LegalQuarterlyReport;
}

export async function upsertLegalQuarterlyReport(
  session: AdminSession,
  payload: UpsertLegalQuarterlyReportPayload,
): Promise<LegalQuarterlyReport> {
  const response = await fetch(`${API_BASE}/reports/legal-quarterly`, {
    method: "PUT",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo guardar informe trimestral legal (${response.status})`);
  return (await response.json()) as LegalQuarterlyReport;
}

export async function retryInvoiceJob(session: AdminSession, jobId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/billing/invoice-jobs/retry`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify({ jobId }),
  });
  if (!response.ok) throw new Error(`No se pudo reintentar job (${response.status})`);
}

export async function searchPaymentDebts(
  session: AdminSession,
  query: string,
): Promise<PaymentDebtSearchItem[]> {
  const response = await fetch(`${API_BASE}/payments/debts/search?q=${encodeURIComponent(query)}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo buscar deudas (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => {
    const customer = (row.customer as Record<string, unknown> | undefined) ?? {};
    const debtSummary = (row.debtSummary as Record<string, unknown> | undefined) ?? {};
    const matchedDocs = ((row.matchedDocuments as Array<Record<string, unknown>> | undefined) ?? []).map((doc) => ({
      id: asString(doc.id),
      folio: asString(doc.folio) || null,
      documentType: asString(doc.documentType),
      status: asString(doc.status),
      dueDate: asString(doc.dueDate),
      outstandingAmount: Number(doc.outstandingAmount ?? 0),
    }));
    return {
      contractId: asString(row.contractId),
      contractNumber: asString(row.contractNumber),
      address: asString(row.address),
      customer: {
        id: asString(customer.id),
        name: asString(customer.name),
        rut: asString(customer.rut),
      },
      debtSummary: {
        documents: Number(debtSummary.documents ?? 0),
        outstandingAmount: Number(debtSummary.outstandingAmount ?? 0),
        overdueAmount: Number(debtSummary.overdueAmount ?? 0),
      },
      matchedDocuments: matchedDocs,
    };
  });
}

export async function getPaymentDebtSummary(
  session: AdminSession,
  contractId: string,
): Promise<PaymentDebtSummary> {
  const response = await fetch(`${API_BASE}/payments/debts/summary?contractId=${encodeURIComponent(contractId)}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo obtener resumen de deuda (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  const customer = (row.customer as Record<string, unknown> | undefined) ?? {};
  const debtSummary = (row.debtSummary as Record<string, unknown> | undefined) ?? {};
  return {
    contractId: asString(row.contractId),
    contractNumber: asString(row.contractNumber),
    customer: {
      id: asString(customer.id),
      name: asString(customer.name),
      rut: asString(customer.rut),
    },
    debtSummary: {
      documents: Number(debtSummary.documents ?? 0),
      outstandingAmount: Number(debtSummary.outstandingAmount ?? 0),
      overdueAmount: Number(debtSummary.overdueAmount ?? 0),
    },
    documents: ((row.documents as Array<Record<string, unknown>> | undefined) ?? []).map((doc) => ({
      id: asString(doc.id),
      folio: asString(doc.folio) || null,
      documentType: asString(doc.documentType),
      status: asString(doc.status),
      dueDate: asString(doc.dueDate),
      totalAmount: Number(doc.totalAmount ?? 0),
      paidAmount: Number(doc.paidAmount ?? 0),
      outstandingAmount: Number(doc.outstandingAmount ?? 0),
    })),
    lastPayment: (() => {
      const lp = row.lastPayment as Record<string, unknown> | null | undefined;
      if (!lp) return null;
      return {
        id: asString(lp.id),
        amount: Number(lp.amount ?? 0),
        paidAt: asString(lp.paidAt),
        receiptNumber: asString(lp.receiptNumber),
        paymentMethodName: lp.paymentMethodName ? asString(lp.paymentMethodName) : null,
      };
    })(),
  };
}

export type PaymentReceiptItem = {
  id: string;
  receiptNumber: string;
  amount: number;
  paidAt: string;
  reference: string | null;
  transactionNumber: string | null;
  backupImageDataUrl: string | null;
  paymentMethod: { name: string } | null;
  contract: {
    contractNumber: string;
    customer: { fullName: string; rut: string };
  };
  allocations: Array<{
    billingDocumentId: string;
    allocatedAmount: number;
    billingDocument: {
      id: string;
      documentType: string;
      folio: string | null;
      dueDate: string;
    };
  }>;
};

export type PaymentVoidPreview = {
  canVoid: boolean;
  blockingReason: string | null;
  payment: {
    id: string;
    receiptNumber: string;
    amount: number;
    paidAt: string;
    reference: string | null;
    source: string;
    paymentMethod: {
      code: string;
      name: string;
    };
    contract: {
      id: string;
      contractNumber: string;
      customerName: string;
      customerRut: string;
    };
    receiptsCount: number;
  };
  documents: Array<{
    billingDocumentId: string;
    documentType: string;
    folio: string | null;
    dueDate: string;
    totalAmount: number;
    allocatedAmount: number;
    currentStatus: string;
    nextStatus: string;
    outstandingAfterVoid: number;
  }>;
  contractBalance: {
    current: {
      currentBalance: number;
      overdueBalance: number;
    };
    projected: {
      currentBalance: number;
      overdueBalance: number;
    };
  };
  cashClosure: {
    id: string;
    closureCode: string;
  } | null;
};

export type PaymentVoidItem = {
  id: string;
  voidCode: string;
  paymentIdOriginal: string;
  receiptNumber: string;
  contractId: string;
  customerId: string;
  amount: number;
  paymentMethodCode: string | null;
  paymentMethodName: string | null;
  source: string;
  paidAtOriginal: string;
  voidedAt: string;
  voidedBy: string | null;
  voidedByName: string | null;
  reason: string;
  snapshot: {
    payment?: {
      id?: string;
      receiptNumber?: string;
      amount?: number;
      paidAt?: string;
      reference?: string | null;
      source?: string;
      paymentMethod?: {
        code?: string;
        name?: string;
      };
    };
    contract?: {
      id?: string;
      contractNumber?: string;
    };
    customer?: {
      id?: string;
      fullName?: string;
      rut?: string;
    };
    allocations?: Array<{
      billingDocumentId?: string;
      allocatedAmount?: number;
      billingDocument?: {
        id?: string;
        documentType?: string;
        folio?: string | null;
        dueDate?: string;
        totalAmount?: number;
        status?: string;
      };
    }>;
    contractBalanceBefore?: {
      currentBalance?: number;
      overdueBalance?: number;
    };
    contractBalanceAfter?: {
      currentBalance?: number;
      overdueBalance?: number;
    };
    reason?: string;
  };
};

function mapPaymentReceipt(row: Record<string, unknown>): PaymentReceiptItem {
  const r = row as Record<string, unknown>;
  const contract = (r.contract ?? {}) as Record<string, unknown>;
  const customer = (contract.customer ?? {}) as Record<string, unknown>;
  const pm = r.paymentMethod ? (r.paymentMethod as Record<string, unknown>) : null;
  const allocations = ((r.allocations as unknown[]) ?? []).map((a) => {
    const al = a as Record<string, unknown>;
    const bd = (al.billingDocument ?? {}) as Record<string, unknown>;
    return {
      billingDocumentId: String(al.billingDocumentId ?? ""),
      allocatedAmount: Number(al.allocatedAmount ?? 0),
      billingDocument: {
        id: String(bd.id ?? ""),
        documentType: String(bd.documentType ?? ""),
        folio: bd.folio ? String(bd.folio) : null,
        dueDate: String(bd.dueDate ?? ""),
      },
    };
  });
  return {
    id: String(r.id ?? ""),
    receiptNumber: String(r.receiptNumber ?? ""),
    amount: Number(r.amount ?? 0),
    paidAt: String(r.paidAt ?? ""),
    reference: r.reference ? String(r.reference) : null,
    transactionNumber: r.transactionNumber ? String(r.transactionNumber) : null,
    backupImageDataUrl: r.backupImageDataUrl ? String(r.backupImageDataUrl) : null,
    paymentMethod: pm ? { name: String(pm.name ?? "") } : null,
    contract: {
      contractNumber: String(contract.contractNumber ?? ""),
      customer: { fullName: String(customer.fullName ?? ""), rut: String(customer.rut ?? "") },
    },
    allocations,
  };
}

function mapPaymentVoidPreview(row: Record<string, unknown>): PaymentVoidPreview {
  const payment = (row.payment as Record<string, unknown> | undefined) ?? {};
  const paymentMethod = (payment.paymentMethod as Record<string, unknown> | undefined) ?? {};
  const contract = (payment.contract as Record<string, unknown> | undefined) ?? {};
  const contractBalance = (row.contractBalance as Record<string, unknown> | undefined) ?? {};
  const currentBalance = (contractBalance.current as Record<string, unknown> | undefined) ?? {};
  const projectedBalance = (contractBalance.projected as Record<string, unknown> | undefined) ?? {};
  const cashClosure = (row.cashClosure as Record<string, unknown> | undefined) ?? null;

  return {
    canVoid: Boolean(row.canVoid),
    blockingReason: row.blockingReason ? asString(row.blockingReason) : null,
    payment: {
      id: asString(payment.id),
      receiptNumber: asString(payment.receiptNumber),
      amount: Number(payment.amount ?? 0),
      paidAt: asString(payment.paidAt),
      reference: payment.reference ? asString(payment.reference) : null,
      source: asString(payment.source),
      paymentMethod: {
        code: asString(paymentMethod.code),
        name: asString(paymentMethod.name),
      },
      contract: {
        id: asString(contract.id),
        contractNumber: asString(contract.contractNumber),
        customerName: asString(contract.customerName),
        customerRut: asString(contract.customerRut),
      },
      receiptsCount: Number(payment.receiptsCount ?? 0),
    },
    documents: ((row.documents as Array<Record<string, unknown>> | undefined) ?? []).map((item) => ({
      billingDocumentId: asString(item.billingDocumentId),
      documentType: asString(item.documentType),
      folio: asString(item.folio) || null,
      dueDate: asString(item.dueDate),
      totalAmount: Number(item.totalAmount ?? 0),
      allocatedAmount: Number(item.allocatedAmount ?? 0),
      currentStatus: asString(item.currentStatus),
      nextStatus: asString(item.nextStatus),
      outstandingAfterVoid: Number(item.outstandingAfterVoid ?? 0),
    })),
    contractBalance: {
      current: {
        currentBalance: Number(currentBalance.currentBalance ?? 0),
        overdueBalance: Number(currentBalance.overdueBalance ?? 0),
      },
      projected: {
        currentBalance: Number(projectedBalance.currentBalance ?? 0),
        overdueBalance: Number(projectedBalance.overdueBalance ?? 0),
      },
    },
    cashClosure: cashClosure
      ? {
          id: asString(cashClosure.id),
          closureCode: asString(cashClosure.closureCode),
        }
      : null,
  };
}

function mapPaymentVoid(row: Record<string, unknown>): PaymentVoidItem {
  const snapshot = (row.snapshot as Record<string, unknown> | undefined) ?? {};
  return {
    id: asString(row.id),
    voidCode: asString(row.voidCode),
    paymentIdOriginal: asString(row.paymentIdOriginal),
    receiptNumber: asString(row.receiptNumber),
    contractId: asString(row.contractId),
    customerId: asString(row.customerId),
    amount: Number(row.amount ?? 0),
    paymentMethodCode: asString(row.paymentMethodCode) || null,
    paymentMethodName: asString(row.paymentMethodName) || null,
    source: asString(row.source),
    paidAtOriginal: asString(row.paidAtOriginal),
    voidedAt: asString(row.voidedAt),
    voidedBy: asString(row.voidedBy) || null,
    voidedByName: asString(row.voidedByName) || null,
    reason: asString(row.reason),
    snapshot: {
      payment: (snapshot.payment as PaymentVoidItem["snapshot"]["payment"]) ?? undefined,
      contract: (snapshot.contract as PaymentVoidItem["snapshot"]["contract"]) ?? undefined,
      customer: (snapshot.customer as PaymentVoidItem["snapshot"]["customer"]) ?? undefined,
      allocations: (snapshot.allocations as PaymentVoidItem["snapshot"]["allocations"]) ?? [],
      contractBalanceBefore: (snapshot.contractBalanceBefore as PaymentVoidItem["snapshot"]["contractBalanceBefore"]) ?? undefined,
      contractBalanceAfter: (snapshot.contractBalanceAfter as PaymentVoidItem["snapshot"]["contractBalanceAfter"]) ?? undefined,
      reason: snapshot.reason ? asString(snapshot.reason) : undefined,
    },
  };
}

function mapCashClosureSnapshot(row: Record<string, unknown>): CashClosureSnapshot {
  return {
    businessDate: asString(row.businessDate),
    generatedAt: asString(row.generatedAt),
    paymentsCount: Number(row.paymentsCount ?? 0),
    movementsCount: Number(row.movementsCount ?? 0),
    totals: {
      payments: Number((row.totals as Record<string, unknown> | undefined)?.payments ?? 0),
      income: Number((row.totals as Record<string, unknown> | undefined)?.income ?? 0),
      expense: Number((row.totals as Record<string, unknown> | undefined)?.expense ?? 0),
      net: Number((row.totals as Record<string, unknown> | undefined)?.net ?? 0),
    },
    byPaymentMethod: ((row.byPaymentMethod as Array<Record<string, unknown>> | undefined) ?? []).map((item) => ({
      code: asString(item.code),
      name: asString(item.name),
      count: Number(item.count ?? 0),
      amount: Number(item.amount ?? 0),
    })),
    byMovementType: ((row.byMovementType as Array<Record<string, unknown>> | undefined) ?? []).map((item) => ({
      type: asString(item.type),
      count: Number(item.count ?? 0),
      amount: Number(item.amount ?? 0),
    })),
    payments: ((row.payments as Array<Record<string, unknown>> | undefined) ?? []).map((item) => ({
      id: asString(item.id),
      paidAt: asString(item.paidAt),
      receiptNumber: asString(item.receiptNumber),
      contractNumber: asString(item.contractNumber),
      customerName: asString(item.customerName),
      paymentMethodCode: asString(item.paymentMethodCode),
      paymentMethodName: asString(item.paymentMethodName),
      amount: Number(item.amount ?? 0),
      reference: item.reference ? asString(item.reference) : null,
    })),
    movements: ((row.movements as Array<Record<string, unknown>> | undefined) ?? []).map((item) => ({
      id: asString(item.id),
      movementDate: asString(item.movementDate),
      createdAt: asString(item.createdAt),
      movementCode: asString(item.movementCode),
      movementType: asString(item.movementType),
      category: asString(item.category),
      description: asString(item.description),
      amount: Number(item.amount ?? 0),
      responsible: asString(item.responsible),
      voucherNumber: item.voucherNumber ? asString(item.voucherNumber) : null,
    })),
  };
}

function mapCashClosure(row: Record<string, unknown>): CashClosureItem {
  const summary = (row.summary as Record<string, unknown> | undefined) ?? {};
  const detail = (row.detail as Record<string, unknown> | undefined) ?? {};
  return {
    id: asString(row.id),
    closureCode: asString(row.closureCode),
    businessDate: asString(row.businessDate),
    closedAt: asString(row.closedAt),
    notes: row.notes ? asString(row.notes) : null,
    paymentsCount: Number(row.paymentsCount ?? 0),
    movementsCount: Number(row.movementsCount ?? 0),
    totalPayments: Number(row.totalPayments ?? 0),
    totalIncome: Number(row.totalIncome ?? 0),
    totalExpense: Number(row.totalExpense ?? 0),
    netTotal: Number(row.netTotal ?? 0),
    closedBy: row.closedBy ? asString(row.closedBy) : null,
    closedByName: row.closedByName ? asString(row.closedByName) : null,
    summary: {
      businessDate: summary.businessDate ? asString(summary.businessDate) : undefined,
      generatedAt: summary.generatedAt ? asString(summary.generatedAt) : undefined,
      totals: summary.totals
        ? {
            payments: Number(((summary.totals as Record<string, unknown>).payments ?? 0)),
            income: Number(((summary.totals as Record<string, unknown>).income ?? 0)),
            expense: Number(((summary.totals as Record<string, unknown>).expense ?? 0)),
            net: Number(((summary.totals as Record<string, unknown>).net ?? 0)),
          }
        : undefined,
      byPaymentMethod: ((summary.byPaymentMethod as Array<Record<string, unknown>> | undefined) ?? []).map((item) => ({
        code: asString(item.code),
        name: asString(item.name),
        count: Number(item.count ?? 0),
        amount: Number(item.amount ?? 0),
      })),
      byMovementType: ((summary.byMovementType as Array<Record<string, unknown>> | undefined) ?? []).map((item) => ({
        type: asString(item.type),
        count: Number(item.count ?? 0),
        amount: Number(item.amount ?? 0),
      })),
      notes: summary.notes ? asString(summary.notes) : null,
    },
    detail: {
      payments: ((detail.payments as Array<Record<string, unknown>> | undefined) ?? []).map((item) => ({
        id: asString(item.id),
        paidAt: asString(item.paidAt),
        receiptNumber: asString(item.receiptNumber),
        contractNumber: asString(item.contractNumber),
        customerName: asString(item.customerName),
        paymentMethodCode: asString(item.paymentMethodCode),
        paymentMethodName: asString(item.paymentMethodName),
        amount: Number(item.amount ?? 0),
        reference: item.reference ? asString(item.reference) : null,
      })),
      movements: ((detail.movements as Array<Record<string, unknown>> | undefined) ?? []).map((item) => ({
        id: asString(item.id),
        movementDate: asString(item.movementDate),
        createdAt: asString(item.createdAt),
        movementCode: asString(item.movementCode),
        movementType: asString(item.movementType),
        category: asString(item.category),
        description: asString(item.description),
        amount: Number(item.amount ?? 0),
        responsible: asString(item.responsible),
        voucherNumber: item.voucherNumber ? asString(item.voucherNumber) : null,
      })),
    },
  };
}

export async function searchPayments(session: AdminSession, query: string): Promise<PaymentReceiptItem[]> {
  const response = await fetch(`${API_BASE}/payments/search?q=${encodeURIComponent(query)}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`Error al buscar pagos (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map(mapPaymentReceipt);
}

export async function getPaymentReceipt(session: AdminSession, id: string): Promise<PaymentReceiptItem> {
  const response = await fetch(`${API_BASE}/payments/${encodeURIComponent(id)}/receipt`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`Error al obtener comprobante (${response.status})`);
  return mapPaymentReceipt((await response.json()) as Record<string, unknown>);
}

export async function getPaymentVoidPreview(session: AdminSession, id: string): Promise<PaymentVoidPreview> {
  const response = await fetch(`${API_BASE}/payments/${encodeURIComponent(id)}/void-preview`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar vista previa de anulacion (${response.status})`);
  return mapPaymentVoidPreview((await response.json()) as Record<string, unknown>);
}

export async function listPaymentVoids(
  session: AdminSession,
  options?: { date?: string; limit?: number },
): Promise<PaymentVoidItem[]> {
  const query = new URLSearchParams();
  if (options?.date?.trim()) query.set("date", options.date.trim());
  if (Number.isFinite(options?.limit)) query.set("limit", String(options?.limit));
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await fetch(`${API_BASE}/payments/voids${suffix}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar historial de anulaciones (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map(mapPaymentVoid);
}

export async function voidPayment(
  session: AdminSession,
  paymentId: string,
  payload: { reason: string },
): Promise<{
  void: PaymentVoidItem;
  affectedDocuments: Array<{
    id: string;
    documentType: string;
    folio: string | null;
    status: string;
    totalAmount: number;
    paidAmount: number;
    outstandingAmount: number;
  }>;
  contractBalance: {
    contractId: string;
    currentBalance: number;
    overdueBalance: number;
  };
}> {
  const response = await fetch(`${API_BASE}/payments/${encodeURIComponent(paymentId)}/void`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo anular pago (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    void: mapPaymentVoid((row.void as Record<string, unknown> | undefined) ?? {}),
    affectedDocuments: ((row.affectedDocuments as Array<Record<string, unknown>> | undefined) ?? []).map((item) => ({
      id: asString(item.id),
      documentType: asString(item.documentType),
      folio: asString(item.folio) || null,
      status: asString(item.status),
      totalAmount: Number(item.totalAmount ?? 0),
      paidAmount: Number(item.paidAmount ?? 0),
      outstandingAmount: Number(item.outstandingAmount ?? 0),
    })),
    contractBalance: {
      contractId: asString((row.contractBalance as Record<string, unknown> | undefined)?.contractId),
      currentBalance: Number(((row.contractBalance as Record<string, unknown> | undefined)?.currentBalance) ?? 0),
      overdueBalance: Number(((row.contractBalance as Record<string, unknown> | undefined)?.overdueBalance) ?? 0),
    },
  };
}

/**
 * Rechazo de un pago con el motivo que da la API.
 *
 * La API responde cosas accionables —"requiere numero de transaccion", "monto
 * excede la deuda", "hay boletas mas antiguas"— y mostrar solo el codigo HTTP
 * deja al cajero adivinando.
 */
async function errorDePago(response: Response, fallback: string): Promise<Error> {
  return errorDeApi(response, fallback);
}

async function errorDeApi(response: Response, fallback: string): Promise<Error> {
  try {
    const cuerpo = (await response.json()) as { message?: string | string[] };
    const detalle = Array.isArray(cuerpo.message) ? cuerpo.message.join(", ") : cuerpo.message;
    if (detalle) return new Error(detalle);
  } catch {
    // sin cuerpo util
  }
  return new Error(`${fallback} (${response.status})`);
}

export async function createManualPayment(
  session: AdminSession,
  payload: {
    contractId: string;
    paymentMethodCode: string;
    documentIds: string[];
    amount?: number;
    settleFull?: boolean;
    reference?: string;
    transactionNumber?: string;
    backupImageDataUrl?: string;
  },
): Promise<{
  payment: {
    id: string;
    receiptNumber: string;
    amount: number;
    paidAt: string;
    reference: string | null;
    transactionNumber?: string | null;
    backupImageDataUrl?: string | null;
  };
  allocations: Array<{ billingDocumentId: string; allocatedAmount: number }>;
  receipt: {
    id: string;
    payloadJson?: Record<string, unknown>;
  };
}> {
  const response = await fetch(`${API_BASE}/payments/manual`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw await errorDePago(response, "No se pudo registrar pago");
  return (await response.json()) as {
    payment: {
      id: string;
      receiptNumber: string;
      amount: number;
      paidAt: string;
      reference: string | null;
      transactionNumber?: string | null;
      backupImageDataUrl?: string | null;
    };
    allocations: Array<{ billingDocumentId: string; allocatedAmount: number }>;
    receipt: {
      id: string;
      payloadJson?: Record<string, unknown>;
    };
  };
}

export async function createManualChargePayment(
  session: AdminSession,
  payload: ManualChargePaymentPayload,
): Promise<{
  payment: {
    id: string;
    receiptNumber: string;
    amount: number;
    paidAt: string;
    reference: string | null;
    transactionNumber?: string | null;
    backupImageDataUrl?: string | null;
  };
  allocations: Array<{ billingDocumentId: string; allocatedAmount: number }>;
  receipt: {
    id: string;
    payloadJson?: Record<string, unknown>;
  };
  billingDocument: {
    id: string;
    folio: string | null;
    documentType: "33" | "34" | "39" | "41";
    totalAmount: number;
    status: string;
  } | null;
}> {
  const response = await fetch(`${API_BASE}/payments/manual-charge`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw await errorDePago(response, "No se pudo registrar pago manual");
  return (await response.json()) as {
    payment: {
      id: string;
      receiptNumber: string;
      amount: number;
      paidAt: string;
      reference: string | null;
      transactionNumber?: string | null;
      backupImageDataUrl?: string | null;
    };
    allocations: Array<{ billingDocumentId: string; allocatedAmount: number }>;
    receipt: {
      id: string;
      payloadJson?: Record<string, unknown>;
    };
    billingDocument: {
      id: string;
      folio: string | null;
      documentType: "33" | "34" | "39" | "41";
      totalAmount: number;
      status: string;
    } | null;
  };
}

export async function getCashClosureSnapshot(
  session: AdminSession,
  options?: { date?: string },
): Promise<CashClosureSnapshot> {
  const query = new URLSearchParams();
  if (options?.date?.trim()) query.set("date", options.date.trim());
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await fetch(`${API_BASE}/payments/cash-closures/snapshot${suffix}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar arqueo de caja (${response.status})`);
  return mapCashClosureSnapshot((await response.json()) as Record<string, unknown>);
}

export async function listCashClosures(
  session: AdminSession,
  options?: { date?: string; limit?: number },
): Promise<CashClosureItem[]> {
  const query = new URLSearchParams();
  if (options?.date?.trim()) query.set("date", options.date.trim());
  if (Number.isFinite(options?.limit)) query.set("limit", String(options?.limit));
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await fetch(`${API_BASE}/payments/cash-closures${suffix}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar historial de cierres (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map(mapCashClosure);
}

export async function createCashClosure(
  session: AdminSession,
  payload?: { date?: string; notes?: string },
): Promise<CashClosureItem> {
  const response = await fetch(`${API_BASE}/payments/cash-closures`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload ?? {}),
  });
  if (!response.ok) throw new Error(`No se pudo registrar cierre de caja (${response.status})`);
  return mapCashClosure((await response.json()) as Record<string, unknown>);
}

export async function listPaymentMethods(session: AdminSession): Promise<PaymentMethodItem[]> {
  const response = await fetch(`${API_BASE}/payment-methods`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar metodos de pago (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
    isCredit: Boolean(row.isCredit),
    status: asString(row.status),
    manualEnabled: Boolean(row.manualEnabled),
    onlineEnabled: Boolean(row.onlineEnabled),
    requiresBackup: Boolean(row.requiresBackup),
  }));
}

export async function createPaymentMethod(
  session: AdminSession,
  payload: {
    code: string;
    name: string;
    isCredit: boolean;
    status: "active" | "inactive";
    manualEnabled: boolean;
    onlineEnabled: boolean;
    requiresBackup: boolean;
  },
): Promise<PaymentMethodItem> {
  const response = await fetch(`${API_BASE}/payment-methods`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear forma de pago (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
    isCredit: Boolean(row.isCredit),
    status: asString(row.status),
    manualEnabled: Boolean(row.manualEnabled),
    onlineEnabled: Boolean(row.onlineEnabled),
    requiresBackup: Boolean(row.requiresBackup),
  };
}

export async function updatePaymentMethod(
  session: AdminSession,
  id: string,
  payload: {
    code: string;
    name: string;
    isCredit: boolean;
    status: "active" | "inactive";
    manualEnabled: boolean;
    onlineEnabled: boolean;
    requiresBackup: boolean;
  },
): Promise<PaymentMethodItem> {
  const response = await fetch(`${API_BASE}/payment-methods/${id}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar forma de pago (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    code: asString(row.code),
    name: asString(row.name),
    isCredit: Boolean(row.isCredit),
    status: asString(row.status),
    manualEnabled: Boolean(row.manualEnabled),
    onlineEnabled: Boolean(row.onlineEnabled),
    requiresBackup: Boolean(row.requiresBackup),
  };
}

export async function deletePaymentMethod(session: AdminSession, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/payment-methods/${id}`, {
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo eliminar forma de pago (${response.status})`);
}

export async function listInstitutionalLoans(session: AdminSession): Promise<InstitutionalLoanItem[]> {
  const response = await fetch(`${API_BASE}/admin/loans`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar préstamos (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => mapInstitutionalLoan(row));
}

export async function createInstitutionalLoan(
  session: AdminSession,
  payload: CreateInstitutionalLoanPayload,
): Promise<InstitutionalLoanItem> {
  const response = await fetch(`${API_BASE}/admin/loans`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear préstamo (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapInstitutionalLoan(row);
}

export async function updateInstitutionalLoanStatus(
  session: AdminSession,
  loanId: string,
  payload: UpdateLoanStatusPayload,
): Promise<InstitutionalLoanItem> {
  const response = await fetch(`${API_BASE}/admin/loans/${encodeURIComponent(loanId)}/status`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar estado de préstamo (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapInstitutionalLoan(row);
}

export async function updateInstitutionalLoanInstallment(
  session: AdminSession,
  loanId: string,
  installmentId: string,
  payload: UpdateLoanInstallmentPayload,
): Promise<InstitutionalLoanItem> {
  const response = await fetch(
    `${API_BASE}/admin/loans/${encodeURIComponent(loanId)}/installments/${encodeURIComponent(installmentId)}`,
    {
      method: "PATCH",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${session.accessToken}`,
        "x-tenant-id": session.tenantId,
      },
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) throw new Error(`No se pudo actualizar cuota (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return mapInstitutionalLoan(row);
}

export async function listPettyCashMovements(
  session: AdminSession,
  options?: { from?: string; to?: string; movementType?: "ingreso" | "egreso"; category?: string; q?: string; limit?: number },
): Promise<PettyCashMovementItem[]> {
  const query = new URLSearchParams();
  if (options?.from?.trim()) query.set("from", options.from.trim());
  if (options?.to?.trim()) query.set("to", options.to.trim());
  if (options?.movementType?.trim()) query.set("movementType", options.movementType.trim());
  if (options?.category?.trim()) query.set("category", options.category.trim());
  if (options?.q?.trim()) query.set("q", options.q.trim());
  if (Number.isFinite(options?.limit)) query.set("limit", String(options?.limit));
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await fetch(`${API_BASE}/admin/petty-cash/movements${suffix}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar movimientos de caja chica (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    id: asString(row.id),
    movementCode: asString(row.movementCode),
    movementDate: asString(row.movementDate),
    movementType: asString(row.movementType) === "ingreso" ? "ingreso" : "egreso",
    category: asString(row.category),
    description: asString(row.description),
    amount: Number(row.amount ?? 0),
    responsible: asString(row.responsible),
    voucherNumber: asString(row.voucherNumber) || null,
    notes: asString(row.notes) || null,
  }));
}

export async function getPettyCashSummary(session: AdminSession): Promise<PettyCashSummary> {
  const response = await fetch(`${API_BASE}/admin/petty-cash/summary`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar resumen de caja chica (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    saldoInicial: Number(row.saldoInicial ?? 0),
    totalIngresos: Number(row.totalIngresos ?? 0),
    totalEgresos: Number(row.totalEgresos ?? 0),
    saldoActual: Number(row.saldoActual ?? 0),
    movimientosIngresos: Number(row.movimientosIngresos ?? 0),
    movimientosEgresos: Number(row.movimientosEgresos ?? 0),
  };
}

export async function createPettyCashMovement(
  session: AdminSession,
  payload: CreatePettyCashMovementPayload,
): Promise<PettyCashMovementItem> {
  const response = await fetch(`${API_BASE}/admin/petty-cash/movements`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear movimiento de caja chica (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    movementCode: asString(row.movementCode),
    movementDate: asString(row.movementDate),
    movementType: asString(row.movementType) === "ingreso" ? "ingreso" : "egreso",
    category: asString(row.category),
    description: asString(row.description),
    amount: Number(row.amount ?? 0),
    responsible: asString(row.responsible),
    voucherNumber: asString(row.voucherNumber) || null,
    notes: asString(row.notes) || null,
  };
}

export async function listAprDocuments(session: AdminSession): Promise<AprDocumentItem[]> {
  const response = await fetch(`${API_BASE}/admin/documents`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo cargar documentación APR (${response.status})`);
  const rows = (await response.json()) as Array<Record<string, unknown>>;
  return rows.map((row) => ({
    id: asString(row.id),
    documentCode: asString(row.documentCode),
    name: asString(row.name),
    category: asDocumentCategory(asString(row.category)),
    description: asString(row.description),
    uploadedAt: asString(row.uploadedAt),
    expiresAt: asString(row.expiresAt) || null,
    sizeLabel: asString(row.sizeLabel),
    mimeType: asString(row.mimeType),
    fileExtension: asDocumentExtension(asString(row.fileExtension)),
    fileUrl: asString(row.fileUrl) || null,
    uploadedBy: asString(row.uploadedBy),
    version: asString(row.version),
    status: asDocumentStatus(asString(row.status)),
    tags: ((row.tags as unknown[]) ?? [])
      .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
      .filter(Boolean),
  }));
}

export async function createAprDocument(
  session: AdminSession,
  payload: CreateAprDocumentPayload,
): Promise<AprDocumentItem> {
  const response = await fetch(`${API_BASE}/admin/documents`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo crear documento APR (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    documentCode: asString(row.documentCode),
    name: asString(row.name),
    category: asDocumentCategory(asString(row.category)),
    description: asString(row.description),
    uploadedAt: asString(row.uploadedAt),
    expiresAt: asString(row.expiresAt) || null,
    sizeLabel: asString(row.sizeLabel),
    mimeType: asString(row.mimeType),
    fileExtension: asDocumentExtension(asString(row.fileExtension)),
    fileUrl: asString(row.fileUrl) || null,
    uploadedBy: asString(row.uploadedBy),
    version: asString(row.version),
    status: asDocumentStatus(asString(row.status)),
    tags: ((row.tags as unknown[]) ?? [])
      .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
      .filter(Boolean),
  };
}

export async function updateAprDocument(
  session: AdminSession,
  id: string,
  payload: UpdateAprDocumentPayload,
): Promise<AprDocumentItem> {
  const response = await fetch(`${API_BASE}/admin/documents/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo actualizar documento APR (${response.status})`);
  const row = (await response.json()) as Record<string, unknown>;
  return {
    id: asString(row.id),
    documentCode: asString(row.documentCode),
    name: asString(row.name),
    category: asDocumentCategory(asString(row.category)),
    description: asString(row.description),
    uploadedAt: asString(row.uploadedAt),
    expiresAt: asString(row.expiresAt) || null,
    sizeLabel: asString(row.sizeLabel),
    mimeType: asString(row.mimeType),
    fileExtension: asDocumentExtension(asString(row.fileExtension)),
    fileUrl: asString(row.fileUrl) || null,
    uploadedBy: asString(row.uploadedBy),
    version: asString(row.version),
    status: asDocumentStatus(asString(row.status)),
    tags: ((row.tags as unknown[]) ?? [])
      .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
      .filter(Boolean),
  };
}

export async function deleteAprDocument(session: AdminSession, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/admin/documents/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudo eliminar documento APR (${response.status})`);
}

export async function listNotifications(
  session: AdminSession,
  limit = 100,
): Promise<{ items: NotificationItem[]; unread: number }> {
  const response = await fetch(`${API_BASE}/notifications?limit=${encodeURIComponent(String(limit))}`, {
    headers: {
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
  });
  if (!response.ok) throw new Error(`No se pudieron cargar notificaciones (${response.status})`);
  const row = (await response.json()) as {
    items?: Array<Record<string, unknown>>;
    summary?: { unread?: number };
  };

  const items = (row.items ?? []).map((input) => ({
    key: asString(input.key),
    category: asNotificationCategory(asString(input.category)),
    type: asNotificationType(asString(input.type)),
    title: asString(input.title),
    description: asString(input.description),
    createdAt: asString(input.createdAt),
    link: asString(input.link),
    read: Boolean(input.read),
  }));

  return {
    items,
    unread: Number(row.summary?.unread ?? items.filter((item) => !item.read).length),
  };
}

export async function markNotificationRead(session: AdminSession, key: string): Promise<void> {
  const response = await fetch(`${API_BASE}/notifications/read`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify({ keys: [key] }),
  });
  if (!response.ok) throw new Error(`No se pudo marcar notificaciÃ³n (${response.status})`);
}

export async function markNotificationsRead(session: AdminSession, keys: string[]): Promise<void> {
  const cleaned = [...new Set(keys.map((key) => key.trim()).filter(Boolean))];
  if (cleaned.length === 0) return;
  const response = await fetch(`${API_BASE}/notifications/read`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify({ keys: cleaned }),
  });
  if (!response.ok) throw new Error(`No se pudieron marcar notificaciones (${response.status})`);
}

export async function markAllNotificationsRead(session: AdminSession): Promise<void> {
  const response = await fetch(`${API_BASE}/notifications/read-all`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify({}),
  });
  if (!response.ok) throw new Error(`No se pudieron marcar todas (${response.status})`);
}

function mapContract(input: Record<string, unknown>): ContractListItem {
  const meter = (input.meter as Record<string, unknown> | undefined) ?? {};
  const tariff = (input.tariff as Record<string, unknown> | undefined) ?? {};
  const customer = (input.customer as Record<string, unknown> | undefined) ?? {};
  return {
    id: asString(input.id),
    customerId: asString(input.customerId),
    meterId: asString(input.meterId),
    sectorId: asString(input.sectorId),
    contractNumber: asString(input.contractNumber),
    contractDate: asString(input.contractDate),
    startAddress: asString(input.startAddress),
    sector: asString(input.sector),
    status: asString(input.status),
    tariffName: asString(tariff.name),
    meterSerialNumber: asString(meter.serialNumber),
    meterType: asString(meter.meterType),
    meterSealNumber: asString(meter.sealNumber),
    customerName: asString(customer.fullName),
    billingRecipientName: asString((input.billingProfile as Record<string, unknown> | undefined)?.recipientName),
    billingDocumentType: asString(input.billingDocumentType),
    billingProfile: mapContractBillingProfile(input.billingProfile as Record<string, unknown> | null | undefined),
  };
}

function mapContractBillingProfile(
  input: Record<string, unknown> | null | undefined,
): ContractBillingProfileItem | null {
  if (!input) return null;
  return {
    id: asString(input.id),
    contractId: asString(input.contractId),
    recipientRut: asString(input.recipientRut),
    recipientDv: asString(input.recipientDv),
    recipientName: asString(input.recipientName),
    businessLine: asString(input.businessLine) || null,
    address: asString(input.address) || null,
    commune: asString(input.commune) || null,
    city: asString(input.city) || null,
    email: asString(input.email) || null,
    notes: asString(input.notes) || null,
  };
}

function mapCustomer(input: Record<string, unknown>): CustomerListItem {
  const customerType = mapCustomerType(asString(input.customerType));
  const contracts = Array.isArray(input.contracts)
    ? input.contracts.map((entry) => {
        const contract = (entry as Record<string, unknown> | undefined) ?? {};
        return {
          id: asString(contract.id),
          contractNumber: asString(contract.contractNumber),
          status: asString(contract.status),
        };
      }).filter((contract) => contract.id || contract.contractNumber)
    : [];
  const firstName = asString(input.firstName);
  const middleName = asString(input.middleName);
  const lastName = asString(input.lastName);
  const fullName =
    asString(input.fullName) ||
    asString(input.businessName) ||
    [firstName, middleName, lastName].filter(Boolean).join(" ").trim();
  const rut = normalizeRut(asString(input.rut), asString(input.dv));
  const status = mapStatus(asString(input.status));

  return {
    id: asString(input.id) || crypto.randomUUID(),
    customerType,
    nombre: fullName || "Sin nombre",
    rut: rut || "-",
    direccion: asString(input.address) || "-",
    sector: "-",
    estado: status,
    telefono: asString(input.phone) || "-",
    email: asString(input.email) || "-",
    medidor: "-",
    deuda: 0,
    contracts,
  };
}

function mapCustomerType(value: string): CustomerListItem["customerType"] {
  if (value === "empresa" || value === "institucion") return value;
  return "domiciliario";
}

function normalizeRut(rut: string, dv: string): string {
  if (!rut && !dv) return "";
  if (rut && dv) return `${rut}-${dv}`;
  return rut || dv;
}

function mapStatus(status: string): CustomerListItem["estado"] {
  if (status === "inactive") return "inactivo";
  return "activo";
}

function asNotificationCategory(value: string): NotificationItem["category"] {
  if (value === "corte" || value === "morosidad" || value === "operaciones" || value === "facturacion" || value === "inventario" || value === "sistema") {
    return value;
  }
  return "sistema";
}

function asNotificationType(value: string): NotificationItem["type"] {
  if (value === "error" || value === "warning" || value === "success" || value === "info") {
    return value;
  }
  return "info";
}

function asDocumentCategory(value: string): AprDocumentItem["category"] {
  if (
    value === "legal" ||
    value === "financiero" ||
    value === "tecnico" ||
    value === "actas" ||
    value === "contratos" ||
    value === "permisos"
  ) {
    return value;
  }
  return "legal";
}

function asDocumentExtension(value: string): AprDocumentItem["fileExtension"] {
  if (value === "pdf" || value === "xlsx" || value === "docx" || value === "jpg" || value === "zip") {
    return value;
  }
  return "pdf";
}

function asDocumentStatus(value: string): AprDocumentItem["status"] {
  if (value === "vigente" || value === "vencido" || value === "revision") {
    return value;
  }
  return "vigente";
}

function mapInstitutionalLoan(row: Record<string, unknown>): InstitutionalLoanItem {
  const installments = ((row.installments as Array<Record<string, unknown>> | undefined) ?? []).map(
    (installment) => ({
      id: asString(installment.id),
      installmentNumber: Number(installment.installmentNumber ?? 0),
      dueDate: asString(installment.dueDate),
      amount: Number(installment.amount ?? 0),
      principalAmount: Number(installment.principalAmount ?? 0),
      interestAmount: Number(installment.interestAmount ?? 0),
      status: asInstallmentStatus(asString(installment.status)),
      paidAt: asString(installment.paidAt) || null,
      reference: asString(installment.reference) || null,
    }),
  );
  return {
    id: asString(row.id),
    loanNumber: asString(row.loanNumber),
    lenderName: asString(row.lenderName),
    loanType: asLoanType(asString(row.loanType)),
    purpose: asString(row.purpose),
    totalAmount: Number(row.totalAmount ?? 0),
    paidAmount: Number(row.paidAmount ?? 0),
    monthlyInstallment: Number(row.monthlyInstallment ?? 0),
    installmentsPaid: Number(row.installmentsPaid ?? 0),
    installmentsTotal: Number(row.installmentsTotal ?? 0),
    interestRate: Number(row.interestRate ?? 0),
    startDate: asString(row.startDate),
    endDate: asString(row.endDate),
    status: asLoanStatus(asString(row.status)),
    responsible: asString(row.responsible),
    collateral: asString(row.collateral) || null,
    notes: asString(row.notes) || null,
    installments,
  };
}

function asLoanType(value: string): InstitutionalLoanItem["loanType"] {
  if (value === "bancario" || value === "fosis" || value === "subdere" || value === "gore" || value === "particular") {
    return value;
  }
  return "bancario";
}

function asLoanStatus(value: string): InstitutionalLoanItem["status"] {
  if (value === "vigente" || value === "pagado" || value === "mora" || value === "refinanciado") {
    return value;
  }
  return "vigente";
}

function asInstallmentStatus(value: string): InstitutionalLoanInstallmentItem["status"] {
  if (value === "pagada" || value === "pendiente" || value === "vencida") {
    return value;
  }
  return "pendiente";
}

function normalizeBillingDocumentType(value: string): "33" | "34" | "39" | "41" {
  return value === "33" || value === "34" || value === "39" || value === "41" ? value : "41";
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

// ─── SAAS SUBSCRIPTION ───────────────────────────────────────────────────────

export type SubscriptionInfo = {
  id: string;
  interval: string;
  status: string;
  nextBillingDate: string | null;
  availableGateways: string[];
  plan: {
    id: string;
    name: string;
    code: string;
    priceMonthly: number;
    priceAnnual: number;
  } | null;
  payments: Array<{
    id: string;
    amount: number;
    status: string;
    dueDate: string;
    paidAt: string | null;
    paymentMethod: string | null;
  }>;
};

export async function getMySubscription(session: AdminSession): Promise<SubscriptionInfo | null> {
  const res = await fetch(`${API_BASE}/auth/subscription`, {
    headers: { authorization: `Bearer ${session.accessToken}`, "x-tenant-id": session.tenantId },
  });
  if (!res.ok) return null;
  return (res.json() as Promise<SubscriptionInfo | null>).catch(() => null);
}

export async function initSubscriptionCheckout(
  session: AdminSession,
  returnUrl: string,
): Promise<{ token: string; url: string }> {
  const res = await fetch(`${API_BASE}/auth/subscription/checkout`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify({ returnUrl }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new Error(String(err.message ?? "Error al iniciar pago"));
  }
  return res.json() as Promise<{ token: string; url: string }>;
}

export async function initKhipuCheckout(
  session: AdminSession,
  returnUrl: string,
): Promise<{ payment_url: string; payment_id: string }> {
  const res = await fetch(`${API_BASE}/auth/subscription/checkout/khipu`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify({ returnUrl }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new Error(String(err.message ?? "Error al iniciar pago Khipu"));
  }
  return res.json() as Promise<{ payment_url: string; payment_id: string }>;
}

export type BoletaSettings = {
  id: string;
  cashDeskHours: string | null;
  officeHours: string | null;
  customerServiceText: string | null;
  emergencyText: string | null;
  whatsappText: string | null;
  readingPhotoPhone: string | null;
  onlinePaymentText: string | null;
  debtInquiryUrl: string | null;
  totemText: string | null;
  bankAccountText: string | null;
  cutNoticeLabel: string | null;
  extraNotes: string[];
};

/** Textos del pie de la boleta. Lo que quede vacio no se imprime. */
export async function getBoletaSettings(session: AdminSession): Promise<BoletaSettings> {
  const response = await fetch(`${API_BASE}/settings/boleta`, {
    headers: { authorization: `Bearer ${session.accessToken}`, "x-tenant-id": session.tenantId },
  });
  if (!response.ok) throw new Error(`No se pudo cargar la boleta (${response.status})`);
  return (await response.json()) as BoletaSettings;
}

export async function updateBoletaSettings(
  session: AdminSession,
  payload: Partial<BoletaSettings>,
): Promise<BoletaSettings> {
  const response = await fetch(`${API_BASE}/settings/boleta`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${session.accessToken}`,
      "x-tenant-id": session.tenantId,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`No se pudo guardar la boleta (${response.status})`);
  return (await response.json()) as BoletaSettings;
}

// ---- Menu Auditoria: linea de tiempo del Libro APR por contrato ----

export type AuditTrailEvent = {
  kind: "movimiento" | "pago" | "anulacion_pago" | "anulacion_documento";
  timestamp: string;
  entryType?: string;
  balanceBucket?: string;
  amount: number;
  folio: string | null;
  documentType: string | null;
  label: string;
  detail?: string | null;
  actor?: string | null;
  isMetadataFix?: boolean;
  runningBalance: { document: number; agreement: number; total: number };
  raw: Record<string, unknown>;
};

export type AuditTrail = {
  contract: { id: string; contractNumber: string; customerName: string; customerRut: string };
  currentBalance: { document: number; agreement: number; total: number } | null;
  eventos: AuditTrailEvent[];
};

export async function getContractAuditTrail(
  session: AdminSession,
  contractId: string,
  options?: { from?: string; to?: string },
): Promise<AuditTrail> {
  const query = new URLSearchParams();
  if (options?.from) query.set("from", options.from);
  if (options?.to) query.set("to", options.to);
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await fetch(`${API_BASE}/ledger/audit/${contractId}${suffix}`, {
    headers: { authorization: `Bearer ${session.accessToken}`, "x-tenant-id": session.tenantId },
  });
  if (!response.ok) throw new Error(`No se pudo cargar la auditoría del contrato (${response.status})`);
  return (await response.json()) as AuditTrail;
}
